import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaCheck,
  FaPrint,
  FaFileExcel,
} from "react-icons/fa";
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n/i18n";
import {
  getQuotations,
  deleteQuotation,
  approveQuotationItems,
  getProjects,
  getCustomers,
  getQuotationDetails,
  clearSuccess,
  clearError,
} from "../../../../redux/Slices/authSlice";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import {
  convertArabicNumbersToEnglish,
  sortListById,
  calculatePdfPagination,
} from "../../../../shared/utils.js/utils";
import PdfPreviewModal from "../Reports/components/PdfPreviewModal";
import {
  saveQuotationsCache,
  getQuotationsCache,
  generateQuotationsCacheKey,
  getAllQuotationsCacheForQuery,
  clearOldQuotationsCache,
  isOnline,
  removeQuotationFromCache,
  saveDropdownCache,
  getDropdownCache,
  generateDropdownCacheKey,
  getAllDropdownCacheForQuery,
  saveCustomersCache,
  getCustomersCache,
  generateCustomersCacheKey,
  getAllCustomersCacheForQuery,
} from "../../../../shared/utils.js/offlineQueue";
import { makeOfflineRequest } from "../../../../shared/utils.js/offlineRequestManager";
import {
  setProjects,
  setProjectsPagination,
  setCustomers,
  setCustomersPagination,
} from "../../../../redux/Slices/authSlice";

// Normalize list response helper
const normalizeListResponse = payload => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  return { items, pagination };
};

const COLORS = {
  primary: "#11324D",
  accent: "#AC2000",
  secondary: "#F6630D",
  neutral: "#B3B3B3",
  light: "#F5F5DC",
};

// STATUS_META will be created inside component to use t() function

const Quotations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const {
    quotations,
    quotationsPagination,
    projects,
    projectsPagination,
    customers,
    customersPagination,
  } = useSelector(state => state.auth);
  const [allQuotations, setAllQuotations] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const offlineToastShownRef = useRef(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [filters, setFilters] = useState({
    projectId: null,
    projectName: "",
    clientLabel: null,
    customerId: null,
    customerName: "",
  });

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const projectSearchRef = useRef(null);
  const projectSearchTimeout = useRef(null);

  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerPage, setCustomerPage] = useState(1);
  const customerSearchRef = useRef(null);
  const customerSearchTimeout = useRef(null);

  const [quotationToDelete, setQuotationToDelete] = useState(null);
  const [quotationToApprove, setQuotationToApprove] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [isGeneratingQuotation, setIsGeneratingQuotation] = useState(false);
  const [isGeneratingPdfAll, setIsGeneratingPdfAll] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const [quotationForPrint, setQuotationForPrint] = useState(null);
  const [previewQuotation, setPreviewQuotation] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { quotationDetails } = useSelector(state => state.auth);
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle("sidenav.quotations");
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [setTitle, dispatch]);

  const handleApplyPerPage = () => {
    const total = filteredQuotations.length || 0;
    const maxValue = total > 0 ? total : 1000;
    const safeValue = Math.max(1, Math.min(maxValue, perPageInput));
    setPerPage(safeValue);
    setCurrentPage(1);
  };

  // Fetch all quotations on load with per_page = total
  // Fetch all quotations on load with per_page = total (modified for offline support)
  const fetchQuotations = async (
    page = currentPage,
    perPageValue = perPage,
  ) => {
    try {
      setIsTableLoading(true);

      const queryParams = {
        page: page,
        per_page: perPageValue,
        project_id: filters.projectId,
        client_label: filters.clientLabel,
        customer_id: filters.customerId,
      };

      const cacheKey = generateQuotationsCacheKey(queryParams);

      // Step 1: try to load from IndexedDB first
      try {
        const cachedData = await getQuotationsCache(cacheKey);
        if (cachedData && Array.isArray(cachedData.quotations)) {
          setAllQuotations(cachedData.quotations);
          setPagination(cachedData.pagination);
        }
      } catch (cacheError) {
        console.error("Quotations: error loading from cache:", cacheError);
      }

      // Step 2: sync with API when online
      if (isOnline()) {
        const result = await dispatch(getQuotations(queryParams));

        if (
          getQuotations.fulfilled &&
          result.type === getQuotations.fulfilled.type
        ) {
          const response = result.payload;
          const data = response?.data || response;
          const paginationData =
            data?.meta || data?.pagination || quotationsPagination || null;
          const total =
            paginationData?.total ||
            (Array.isArray(data?.data)
              ? data.data.length
              : Array.isArray(data)
                ? data.length
                : 0);

          if (total > 0) {
            // Fetch all quotations if needed, or if result is already all data
            // Note: The previous logic fetched ALL data if total > 0.
            // We'll stick to pagination if possible, but the original code fetched all.
            // Let's optimize: if we asked for 10 and got 10, maybe we don't need all immediately unless specific requirement.
            // However, to keep consistency with previous logic which fetched ALL:
            const allParams = {
              ...queryParams,
              page: 1,
              per_page: total,
            };

            const allResult = await dispatch(getQuotations(allParams));
            if (
              getQuotations.fulfilled &&
              allResult.type === getQuotations.fulfilled.type
            ) {
              const allResponse = allResult.payload;
              const allData = allResponse?.data || allResponse;
              let quotationsList = [];
              if (Array.isArray(allData?.data)) {
                quotationsList = allData.data;
              } else if (Array.isArray(allData)) {
                quotationsList = allData;
              }
              setAllQuotations(quotationsList);
              setPagination(paginationData);

              // Update cache
              await saveQuotationsCache(
                cacheKey,
                quotationsList,
                paginationData,
              );
            }
          } else {
            setAllQuotations([]);
            setPagination(null);
            await saveQuotationsCache(cacheKey, [], null);
          }
        }
      } else {
        // Offline: rely on cache if available
        const allCached = await getAllQuotationsCacheForQuery(queryParams);
        if (Array.isArray(allCached) && allCached.length > 0) {
          setAllQuotations(allCached);
          // Approximate pagination from cached data if needed, or just set generic
          if (!pagination) {
            setPagination({
              total: allCached.length,
              per_page: perPageValue,
              current_page: page,
              last_page: Math.ceil(allCached.length / perPageValue),
            });
          }

          if (!offlineToastShownRef.current) {
            offlineToastShownRef.current = true;
            toast.info(
              isRTL
                ? "لا يوجد اتصال بالإنترنت. يتم عرض بيانات عروض الأسعار المحفوظة محلياً."
                : "No internet connection. Showing locally saved quotations.",
              { rtl: isRTL, autoClose: 3000 },
            );
          }
        } else {
          if (!offlineToastShownRef.current) {
            offlineToastShownRef.current = true;
            toast.warning(
              isRTL
                ? "لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة."
                : "No internet connection and no locally saved quotations.",
              { rtl: isRTL, autoClose: 3000 },
            );
          }
          // Keep existing state if any, or clear
          if (allQuotations.length === 0) {
            setAllQuotations([]);
          }
        }
      }
    } catch (error) {
      console.error("Quotations fetch error:", error);
      // Try to load cache on error as fallback
      try {
        const queryParams = {
          page: page,
          per_page: perPageValue,
          project_id: filters.projectId,
          client_label: filters.clientLabel,
          customer_id: filters.customerId,
        };
        const allCached = await getAllQuotationsCacheForQuery(queryParams);
        if (Array.isArray(allCached) && allCached.length > 0) {
          setAllQuotations(allCached);
          if (!offlineToastShownRef.current) {
            offlineToastShownRef.current = true;
            toast.warning(
              isRTL
                ? "حدث خطأ في الاتصال. يتم عرض البيانات المحفوظة."
                : "Connection error. Showing cached data.",
              { rtl: isRTL, autoClose: 3000 },
            );
          }
        } else {
          toast.error(error?.message || t("quotations.error.fetch"), {
            rtl: isRTL,
          });
        }
      } catch (e) {
        toast.error(error?.message || t("quotations.error.fetch"), {
          rtl: isRTL,
        });
      }
    } finally {
      setIsTableLoading(false);
    }
  };

  // Clear old cache once and fetch on mount / pagination change
  useEffect(() => {
    clearOldQuotationsCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchQuotations(currentPage, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    i18n.language,
    filters.projectId,
    filters.clientLabel,
    filters.customerId,
    currentPage,
    perPage,
  ]);

  // Auto-refresh when coming back online
  useEffect(() => {
    const handleOnline = () => {
      fetchQuotations(currentPage, perPage);
    };

    const handleOfflineRequestsRetried = event => {
      const detail = event?.detail || {};
      if (detail.count > 0 && detail.results && detail.results.length > 0) {
        const hasSuccessfulRetries = detail.results.some(
          r => r.success === true,
        );
        if (hasSuccessfulRetries) {
          const hasQuotationUpdate = detail.results.some(r =>
            r.request?.url?.includes("/quotations"),
          );
          if (hasQuotationUpdate) {
            fetchQuotations(currentPage, perPage);
          }
        }
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener(
      "offlineRequestsRetried",
      handleOfflineRequestsRetried,
    );

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener(
        "offlineRequestsRetried",
        handleOfflineRequestsRetried,
      );
    };
  }, [currentPage, perPage]);

  useEffect(() => {
    if (!projectDropdownOpen) return;

    const loadProjects = async () => {
      try {
        const queryParams = {
          page: projectPage,
          per_page: 10,
          search: projectSearchTerm || undefined,
        };
        const cacheKey = generateDropdownCacheKey("projects", queryParams);

        // Try cache first
        try {
          const cachedData = await getDropdownCache(cacheKey);
          if (cachedData && Array.isArray(cachedData.items)) {
            // We need to update Redux state directly if we have cached data,
            // but since we are using useSelector, we might need to dispatch actions to update state
            // IF the slice exports them. I'll need to check authSlice exports.
            // For now assuming we can dispatch actions or we need to locally manage this data if actions aren't available.
            // However, reusing the existing Redux flow + Cache is better.
            dispatch(setProjects(cachedData.items));
            dispatch(setProjectsPagination(cachedData.pagination));
          }
        } catch (e) {
          console.error("Cache error", e);
        }

        if (isOnline()) {
          const result = await dispatch(getProjects(queryParams)).unwrap();

          // Save to cache
          if (result && (result.data || result.items)) {
            const data = result.data?.data || result.data || result.items || [];
            const meta = result.data?.meta || result.pagination || result.meta;
            await saveDropdownCache(cacheKey, data, meta);
          }
        } else {
          // Offline fallback if cache was empty or partial
          const allCached = await getAllDropdownCacheForQuery(
            "projects",
            queryParams,
          );
          if (allCached.length > 0) {
            dispatch(setProjects(allCached));
          }
        }
      } catch {
        // errors handled globally
      }
    };

    loadProjects();
  }, [projectDropdownOpen, projectPage, projectSearchTerm, dispatch]);

  useEffect(() => {
    if (projectDropdownOpen && projectSearchRef.current) {
      projectSearchRef.current.focus();
    }
  }, [projectDropdownOpen]);

  useEffect(() => {
    if (!customerDropdownOpen || filters.clientLabel === "contractor") return;

    const loadCustomers = async () => {
      try {
        const queryParams = {
          page: customerPage,
          per_page: 10,
          search: customerSearchTerm || undefined,
        };
        const cacheKey = generateCustomersCacheKey(queryParams);

        // Try load from cache
        try {
          const cachedData = await getCustomersCache(cacheKey);
          if (cachedData && Array.isArray(cachedData.customers)) {
            dispatch(setCustomers(cachedData.customers));
            dispatch(setCustomersPagination(cachedData.pagination));
          }
        } catch (e) {
          console.error("Cache error", e);
        }

        if (isOnline()) {
          const result = await dispatch(getCustomers(queryParams)).unwrap();

          // Save to cache
          if (result && (result.data || result.items)) {
            const data = result.data?.data || result.data || result.items || [];
            const meta = result.data?.meta || result.pagination || result.meta;
            await saveCustomersCache(cacheKey, data, meta);
          }
        } else {
          // Offline fallback
          const allCached = await getAllCustomersCacheForQuery(queryParams);
          if (allCached.length > 0) {
            dispatch(setCustomers(allCached));
          }
        }
      } catch {
        // errors handled globally
      }
    };

    loadCustomers();
  }, [
    customerDropdownOpen,
    customerPage,
    customerSearchTerm,
    filters.clientLabel,
    dispatch,
  ]);

  useEffect(() => {
    if (customerDropdownOpen && customerSearchRef.current) {
      customerSearchRef.current.focus();
    }
  }, [customerDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        customerDropdownOpen &&
        !event.target.closest("[data-customer-dropdown]")
      ) {
        setCustomerDropdownOpen(false);
        setCustomerSearchTerm("");
        setCustomerPage(1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [customerDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        projectDropdownOpen &&
        !event.target.closest("[data-project-dropdown]")
      ) {
        setProjectDropdownOpen(false);
        setProjectSearchTerm("");
        setProjectPage(1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [projectDropdownOpen]);

  const handleProjectSearch = value => {
    setProjectSearchTerm(value);
    if (projectSearchTimeout.current) {
      clearTimeout(projectSearchTimeout.current);
    }
    projectSearchTimeout.current = setTimeout(() => {
      setProjectPage(1);
    }, 250);
  };

  const handleCustomerSearch = value => {
    setCustomerSearchTerm(value);
    if (customerSearchTimeout.current) {
      clearTimeout(customerSearchTimeout.current);
    }
    customerSearchTimeout.current = setTimeout(() => {
      setCustomerPage(1);
    }, 250);
  };

  const handleSendToClient = async quotation => {
    try {
      // Use offline-aware request: POST /quotations/:id/send-to-client with empty JSON body
      await makeOfflineRequest(
        `/quotations/${quotation.id}/send-to-client`,
        "POST",
        {},
      );
      toast.success(
        t("quotations.send.success", {
          defaultValue: "Quotation sent to client successfully",
        }),
        {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
      dispatch(clearSuccess());
      fetchQuotations();
    } catch (error) {
      if (error?.message === "OFFLINE_REQUEST_SAVED") {
        toast.info(
          isRTL
            ? "سيتم إرسال عرض السعر تلقائياً عند عودة الاتصال بالإنترنت"
            : "Quotation will be sent automatically when connection is restored",
          { rtl: isRTL, autoClose: 4000 },
        );
        dispatch(clearError());
      } else {
        toast.error(
          error ||
          t("quotations.send.error", {
            defaultValue: "Failed to send quotation to client",
          }),
          {
            rtl: isRTL,
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
        dispatch(clearError());
      }
    }
  };

  const handleApproveItems = quotation => {
    setQuotationToApprove(quotation);
    setSelectedItemIds([]);
  };

  const handleToggleItem = itemId => {
    setSelectedItemIds(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleConfirmApprove = async () => {
    if (!quotationToApprove || selectedItemIds.length === 0) {
      toast.error(
        t("quotations.approve.noItems", {
          defaultValue: "Please select items to approve",
        }),
        {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
      return;
    }
    try {
      // اعتماد البنود عبر redux thunk
      await dispatch(
        approveQuotationItems({
          id: quotationToApprove.id,
          item_ids: selectedItemIds,
        }),
      ).unwrap();
      toast.success(
        t("quotations.approve.success", {
          defaultValue: "Quotation items approved successfully",
        }),
        {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
      dispatch(clearSuccess());
      setQuotationToApprove(null);
      setSelectedItemIds([]);
      fetchQuotations();
    } catch (error) {
      if (error?.message === "OFFLINE_REQUEST_SAVED") {
        toast.info(
          isRTL
            ? "سيتم اعتماد العناصر تلقائياً عند عودة الاتصال بالإنترنت"
            : "Approval will be processed automatically when connection is restored",
          { rtl: isRTL, autoClose: 4000 },
        );
        dispatch(clearError());
        setQuotationToApprove(null);
        setSelectedItemIds([]);
      } else {
        toast.error(
          error ||
          t("quotations.approve.error", {
            defaultValue: "Failed to approve quotation items",
          }),
          {
            rtl: isRTL,
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
        dispatch(clearError());
      }
    }
  };

  /* Delete Handler with Offline Support */
  const handleDelete = async () => {
    if (!quotationToDelete) return;
    const idToDelete = quotationToDelete.id;
    const quotationToDeleteBackup = quotationToDelete;
    // Optimistically remove from local state first
    setAllQuotations(prev =>
      prev.filter(quotation => quotation.id !== idToDelete),
    );
    setQuotationToDelete(null);

    try {
      // Try to use offline request manager first
      await makeOfflineRequest(`/quotations/${idToDelete}`, "DELETE", null);
      toast.success(
        t("quotations.delete.success", {
          defaultValue: "Quotation deleted successfully",
        }),
        {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
        },
      );

      // Update IndexedDB cache after successful deletion
      try {
        await removeQuotationFromCache(idToDelete);
        if (pagination && pagination.total > 0) {
          setPagination({
            ...pagination,
            total: Math.max(0, pagination.total - 1),
          });
        }
      } catch (cacheError) {
        console.error("Error updating cache:", cacheError);
      }
    } catch (error) {
      // If request was saved offline, show appropriate message
      if (error.message === "OFFLINE_REQUEST_SAVED") {
        try {
          await removeQuotationFromCache(idToDelete);
        } catch (e) { }

        toast.info(
          isRTL
            ? "سيتم حذف عرض السعر عند عودة الاتصال بالإنترنت"
            : "Quotation will be deleted when connection is restored",
          { rtl: isRTL, autoClose: 5000 },
        );
        return;
      }

      setAllQuotations(prev => {
        const exists = prev.find(quotation => quotation.id === idToDelete);
        if (!exists) {
          return [...prev, quotationToDeleteBackup].sort((a, b) => {
            if (a.id && b.id) return a.id - b.id;
            return 0;
          });
        }
        return prev;
      });

      // If it's a client error (4xx) from makeOfflineRequest or other error
      if (error?.message !== "OFFLINE_REQUEST_SAVED") {
        const errorMessage =
          error?.message ||
          t("quotations.delete.error", {
            defaultValue: "Failed to delete quotation",
          });
        toast.error(errorMessage, { rtl: isRTL });
      }
    }
  };

  // Local pagination + client-side filter safety net (API filters also applied via slice)
  const getClientRelationId = (quotation, clientLabel) => {
    if (clientLabel === "contractor") {
      return (
        quotation?.contractor_id ??
        quotation?.contractor?.id ??
        quotation?.client_id ??
        quotation?.customer_id ??
        quotation?.customer?.id ??
        null
      );
    }
    return (
      quotation?.customer_id ??
      quotation?.customer?.id ??
      quotation?.contractor_id ??
      quotation?.contractor?.id ??
      null
    );
  };

  const filteredQuotations = useMemo(() => {
    let list = Array.isArray(allQuotations) ? [...allQuotations] : [];
    if (filters.projectId) {
      list = list.filter(
        q =>
          String(q.project_id || q.project?.id) === String(filters.projectId),
      );
    }
    if (filters.clientLabel) {
      list = list.filter(
        q =>
          String(q.client_label || q.client_type || "").toLowerCase() ===
          String(filters.clientLabel).toLowerCase(),
      );
    }
    if (filters.customerId) {
      list = list.filter(
        q =>
          String(getClientRelationId(q, filters.clientLabel)) ===
          String(filters.customerId),
      );
    }
    return sortListById(list, "desc");
  }, [
    allQuotations,
    filters.projectId,
    filters.clientLabel,
    filters.customerId,
  ]);

  const clientFilterOptions = useMemo(() => {
    if (filters.clientLabel !== "contractor") {
      return Array.isArray(customers) ? customers : [];
    }

    const search = customerSearchTerm.trim().toLowerCase();
    const seen = new Set();

    return (Array.isArray(allQuotations) ? allQuotations : [])
      .map(quotation => {
        const contractor = quotation?.contractor;
        const id = quotation?.contractor_id ?? contractor?.id;
        if (id === undefined || id === null || seen.has(String(id))) return null;
        seen.add(String(id));
        return {
          id,
          code: contractor?.code || quotation?.contractor_code || "",
          name_ar:
            contractor?.name_ar || quotation?.contractor_name_ar || "",
          name_en:
            contractor?.name_en ||
            quotation?.contractor_name_en ||
            quotation?.contractor_name ||
            "",
          name:
            contractor?.name ||
            quotation?.contractor_name ||
            contractor?.name_ar ||
            contractor?.name_en ||
            "",
        };
      })
      .filter(Boolean)
      .filter(contractor => {
        if (!search) return true;
        return [contractor.code, contractor.name, contractor.name_ar, contractor.name_en]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(search));
      });
  }, [allQuotations, customers, customerSearchTerm, filters.clientLabel]);
  const currentPageSafe = Math.max(1, currentPage);
  const totalPages = Math.ceil(filteredQuotations.length / perPage);
  const startIndex = (currentPageSafe - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedQuotations = filteredQuotations.slice(startIndex, endIndex);

  const resetFilters = () => {
    setFilters({
      projectId: null,
      projectName: "",
      clientLabel: null,
      customerId: null,
      customerName: "",
    });
    setProjectSearchTerm("");
    setProjectPage(1);
    setCustomerSearchTerm("");
    setCustomerPage(1);
    setCurrentPage(1);
  };

  // Handle preview quotation PDF
  const handlePreviewQuotation = async quotation => {
    try {
      // Fetch quotation details if not already loaded
      if (!quotationDetails || quotationDetails.id !== quotation.id) {
        await dispatch(getQuotationDetails(quotation.id)).unwrap();
      }
      setPreviewQuotation(quotation);
      setIsPreviewOpen(true);
      // Wait for state to update
      await new Promise(resolve => requestAnimationFrame(() => resolve()));
    } catch (error) {
      toast.error(t("accountingReports.errors.fetchError"), { rtl: isRTL });
    }
  };

  const activeFiltersForPdf = useMemo(() => {
    const parts = [];
    if (filters.projectName) {
      parts.push(`${t('quotations.tableHeaders.project', { defaultValue: 'Project' })}: ${filters.projectName}`);
    }
    if (filters.customerName) {
      const label = filters.clientLabel === 'contractor'
        ? t('quotations.tableHeaders.contractor', { defaultValue: 'Contractor' })
        : t('quotations.tableHeaders.customer', { defaultValue: 'Customer' });
      parts.push(`${label}: ${filters.customerName}`);
    }
    return parts.join(' | ');
  }, [filters.projectName, filters.customerName, filters.clientLabel, t]);

  // ─── PDF Pagination Engine (shared calculatePdfPagination) ──────────────────
  const buildDynamicPdfChunks = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      setPdfChunks([]);
      return [];
    }
    // Measure heights from off-screen measurement template elements
    const topHeaderEl = document.getElementById('quotations-pdf-measure-top-header');
    const theadEl = document.getElementById('quotations-pdf-measure-thead');
    const footerEl = document.getElementById('quotations-pdf-measure-footer');
    const sampleRow = document.getElementById('quotations-pdf-measure-tbody')?.querySelector('tr');
    const headerHeight = topHeaderEl?.offsetHeight || 180;
    const tableHeaderHeight = theadEl?.offsetHeight || 32;
    const footerHeight = footerEl?.offsetHeight || 120;
    const rowHeight = sampleRow?.offsetHeight || 28;
    // Calculate available vertical space for data rows
    const footerStartY = 1033 - 10 - footerHeight - 15;
    const rowsStartY = 18 + headerHeight + tableHeaderHeight;
    const fallbackSize = Math.max(1, Math.floor((footerStartY - rowsStartY) / rowHeight));
    // Pass null elements → calculatePdfPagination uses its chunkArray fallback
    const chunks = calculatePdfPagination(items, null, { fallbackSize });
    setPdfChunks(chunks);
    return chunks;
  };

  // Handle preview all quotations
  const handlePreviewAll = async () => {
    try {
      let allQuotationsForPreview = [];
      if (isOnline()) {
        try {
          const total = pagination?.total || 1000;
          const allParams = {
            page: 1,
            per_page: total,
            project_id: filters.projectId || undefined,
            client_label: filters.clientLabel || undefined,
            customer_id: filters.customerId || undefined,
          };
          const result = await dispatch(getQuotations(allParams));
          if (getQuotations.fulfilled.match(result)) {
            const response = result.payload;
            const data = response?.data || response;
            if (Array.isArray(data?.data)) {
              allQuotationsForPreview = data.data;
            } else if (Array.isArray(data)) {
              allQuotationsForPreview = data;
            }
          }
        } catch (apiError) {
          allQuotationsForPreview = filteredQuotations || [];
        }
      } else {
        allQuotationsForPreview = filteredQuotations || [];
      }

      if (!allQuotationsForPreview || allQuotationsForPreview.length === 0) {
        toast.error(t('quotations.preview.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
        return;
      }

      await new Promise((resolve) => requestAnimationFrame(resolve));
      const chunks = buildDynamicPdfChunks(allQuotationsForPreview);
      if (!chunks || chunks.length === 0) return;
      setPdfType('all');
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      setIsPreviewOpen(true);
    } catch (err) {
      console.error('Preview error:', err);
      toast.error(t('quotations.preview.loadError', { defaultValue: 'Failed to load preview' }), { rtl: isRTL });
    }
  };

  // Dragging and zoom logic is now handled by PdfPreviewModal

  // Handle print from preview
  const handlePrintFromPreview = async () => {
    if (!previewQuotation) return;
    if (previewQuotation.all) {
      await handlePrintAll();
    } else {
      await handlePrintQuotation(previewQuotation);
    }
  };

  const handlePrintQuotation = async quotation => {
    try {
      // Fetch quotation details if not already loaded
      if (!quotationDetails || quotationDetails.id !== quotation.id) {
        await dispatch(getQuotationDetails(quotation.id)).unwrap();
      }
      setQuotationForPrint(quotation);
      await new Promise(resolve => requestAnimationFrame(() => resolve()));

      // Wait for fonts to load
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));

      setIsGeneratingQuotation(true);

      const element = document.getElementById("quotation-pdf");
      if (!element) {
        toast.error(t("quotations.print.error"));
        return;
      }

      // Shared PDF Engine — capture + assembly
      await generateSingleElementPdf({
        elementId: element.id,
        fileName: `ACE_Quotation_${quotation.quotation_no || quotation.quotation_number || quotation.id}_${new Date().toISOString().split("T")[0]}.pdf`,
        isRTL,
      });
      setPdfType("single");
    } catch (err) {
      console.error("Quotation PDF error:", err);
      toast.error(t("accountingReports.errors.printError"), { rtl: isRTL });
    } finally {
      setIsGeneratingQuotation(false);
      setQuotationForPrint(null);
      setPdfType(null);
    }
  };

  // Preload image helper
  const preloadImage = src =>
    new Promise(resolve => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  // Handle print all quotations
  const handlePrintAll = async () => {
    try {
      let allQuotationsForPrint = [];
      if (isOnline()) {
        try {
          const total = pagination?.total || 1000;
          const allParams = {
            page: 1,
            per_page: total,
            project_id: filters.projectId || undefined,
            client_label: filters.clientLabel || undefined,
            customer_id: filters.customerId || undefined,
          };
          const result = await dispatch(getQuotations(allParams));
          if (getQuotations.fulfilled.match(result)) {
            const response = result.payload;
            const data = response?.data || response;
            if (Array.isArray(data?.data)) {
              allQuotationsForPrint = data.data;
            } else if (Array.isArray(data)) {
              allQuotationsForPrint = data;
            }
          }
        } catch (apiError) {
          allQuotationsForPrint = filteredQuotations || [];
        }
      } else {
        allQuotationsForPrint = filteredQuotations || [];
      }

      if (!allQuotationsForPrint || allQuotationsForPrint.length === 0) {
        toast.error(t('quotations.print.noData', { defaultValue: 'No data available for printing' }), { rtl: isRTL });
        return;
      }

      setIsGeneratingPdfAll(true);
      const chunks = buildDynamicPdfChunks(allQuotationsForPrint);
      setPdfType('all');

      await new Promise((resolve) => setTimeout(resolve, 800));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = chunks.map((_, i) => `quotations-pdf-all-${i}`);
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_Quotations_All_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('customers.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Quotations PDF error:', error);
      toast.error(t('customers.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdfAll(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // Handle export single quotation to Excel
  const handleExportQuotationToExcel = quotation => {
    const projectName = isRTL
      ? quotation.project?.name_ar || quotation.project?.name_en || "—"
      : quotation.project?.name_en || quotation.project?.name_ar || "—";

    const data = [
      {
        [t("quotations.tableHeaders.quotationNumber", {
          defaultValue: "رقم العرض",
        })]:
          quotation.quotation_no ||
          quotation.quotation_number ||
          quotation.id ||
          "—",
        [t("quotations.tableHeaders.project", { defaultValue: "المشروع" })]:
          projectName,
        [t("quotations.tableHeaders.itemsCount", {
          defaultValue: "عدد البنود",
        })]: quotation.items?.length || 0,
        [t("quotations.tableHeaders.total", { defaultValue: "الإجمالي" })]:
          formatCurrency(
            quotation.total_amount ||
            quotation.subtotal_amount ||
            quotation.subtotal,
          ),
        [t("quotations.tableHeaders.status", { defaultValue: "الحالة" })]:
          renderStatusText(quotation.status),
        [t("quotations.tableHeaders.createdAt", {
          defaultValue: "تاريخ الإنشاء",
        })]: formatDate(quotation.created_at),
        [t("quotations.tableHeaders.sentAt", {
          defaultValue: "تاريخ الإرسال",
        })]: formatDate(quotation.sent_at),
      },
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      t("quotations.export.sheetName", { defaultValue: "عرض السعر" }),
    );
    const identifier =
      quotation.quotation_no || quotation.quotation_number || quotation.id;
    const fileName = `${t("quotations.export.fileName", { defaultValue: "عرض_السعر" })}_${identifier}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("quotations.export.success", {
        defaultValue: "تم تصدير البيانات بنجاح",
      }),
    );
  };

  // Handle export all quotations to Excel
  const handleExportAllToExcel = async () => {
    if (!filteredQuotations || filteredQuotations.length === 0) {
      toast.error(
        t("quotations.export.noData", { defaultValue: "No data available" }),
        {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
      return;
    }

    try {
      const data = filteredQuotations.map((quotation, index) => {
        const projectName = isRTL
          ? quotation.project?.name_ar || quotation.project?.name_en || "—"
          : quotation.project?.name_en || quotation.project?.name_ar || "—";
        return {
          [t("common.serialNumber", { defaultValue: "#" })]: index + 1,
          [t("quotations.tableHeaders.quotationNumber", {
            defaultValue: "Quotation Number",
          })]:
            quotation.quotation_no ||
            quotation.quotation_number ||
            quotation.id ||
            "—",
          [t("quotations.tableHeaders.project", { defaultValue: "Project" })]:
            projectName,
          [t("quotations.tableHeaders.itemsCount", {
            defaultValue: "Items Count",
          })]: quotation.items?.length || 0,
          [t("quotations.tableHeaders.total", { defaultValue: "Total" })]:
            formatCurrency(
              quotation.total_amount ||
              quotation.subtotal_amount ||
              quotation.subtotal,
            ),
          [t("quotations.tableHeaders.status", { defaultValue: "Status" })]:
            renderStatusText(quotation.status),
          [t("quotations.tableHeaders.createdAt", {
            defaultValue: "Created At",
          })]: formatDate(quotation.created_at),
          [t("quotations.tableHeaders.sentAt", { defaultValue: "Sent At" })]:
            formatDate(quotation.sent_at),
        };
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        t("quotations.excel.sheetName", { defaultValue: "Quotations" }),
      );
      const fileName = `${t("quotations.excel.fileName", { defaultValue: "Quotations" })}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(
        t("quotations.export.success", {
          defaultValue: "Data exported successfully",
        }),
        {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    } catch (err) {
      console.error("Export error:", err);
      toast.error(
        t("quotations.export.error", {
          defaultValue: "An error occurred while exporting",
        }),
        {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    }
  };

  const getStatusMeta = status => {
    const statusMap = {
      draft: {
        label: t("quotations.status.draft", { defaultValue: "مسودة" }),
        color: COLORS.neutral,
        bg: "rgba(179,179,179,0.2)",
      },
      sent_to_client: {
        label: t("quotations.status.sentToClient", {
          defaultValue: "تم الإرسال",
        }),
        color: COLORS.secondary,
        bg: "rgba(246,99,13,0.2)",
      },
      approved: {
        label: t("quotations.status.approved", { defaultValue: "معتمد" }),
        color: "#27ae60",
        bg: "rgba(39,174,96,0.2)",
      },
      rejected: {
        label: t("quotations.status.rejected", { defaultValue: "مرفوض" }),
        color: "#c0392b",
        bg: "rgba(192,57,43,0.2)",
      },
    };
    return (
      statusMap[status] || {
        label: status || "—",
        color: COLORS.neutral,
        bg: "rgba(179,179,179,0.2)",
      }
    );
  };

  const renderStatusText = status => {
    return getStatusMeta(status).label;
  };

  const quotationForPrintData =
    quotationForPrint ||
    (quotationDetails && quotations?.find(q => q.id === quotationDetails.id)) ||
    null;
  const printQuotationDetails =
    quotationDetails && quotationDetails.id === quotationForPrintData?.id
      ? quotationDetails
      : null;

  const quotationDate = useMemo(() => {
    const date =
      printQuotationDetails?.created_at ||
      quotationForPrintData?.created_at ||
      new Date();
    try {
      return new Date(date).toLocaleDateString("en-US", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return new Date(date).toISOString().split("T")[0];
    }
  }, [printQuotationDetails, quotationForPrintData]);

  // Get client label and customer name for print template
  const clientLabel =
    printQuotationDetails?.client_label || quotationForPrintData?.client_label;
  const customerName =
    printQuotationDetails?.customer_name ||
    quotationForPrintData?.customer_name;
  const contractorName =
    clientLabel === "contractor" && customerName
      ? customerName
      : clientLabel === "customer" && customerName
        ? customerName
        : printQuotationDetails?.contractor?.name_ar ||
        printQuotationDetails?.contractor?.name_en ||
        quotationForPrintData?.contractor?.name_ar ||
        quotationForPrintData?.contractor?.name_en ||
        "—";
  const clientLabelText =
    clientLabel === "contractor"
      ? t("quotations.filter.contractor", { defaultValue: "مقاول" })
      : clientLabel === "customer"
        ? t("quotations.filter.customer", { defaultValue: "عميل" })
        : t("quotations.filter.contractor", { defaultValue: "مقاول" });
  const quotationSubject =
    printQuotationDetails?.subject ||
    quotationForPrintData?.subject ||
    t("quotations.pdf.quotationSubject", { defaultValue: "عرض سعر" });
  const quotationProject = isRTL
    ? printQuotationDetails?.project?.name_ar ||
    printQuotationDetails?.project?.name_en ||
    quotationForPrintData?.project?.name_ar ||
    quotationForPrintData?.project?.name_en ||
    "—"
    : printQuotationDetails?.project?.name_en ||
    printQuotationDetails?.project?.name_ar ||
    quotationForPrintData?.project?.name_en ||
    quotationForPrintData?.project?.name_ar ||
    "—";
  const quotationLocation =
    printQuotationDetails?.location || quotationForPrintData?.location || "—";
  const quotationRevision =
    printQuotationDetails?.quotation_no ||
    printQuotationDetails?.quotation_number ||
    quotationForPrintData?.quotation_no ||
    quotationForPrintData?.quotation_number ||
    "—";
  const quotationNotes = printQuotationDetails?.notes
    ? Array.isArray(printQuotationDetails.notes)
      ? printQuotationDetails.notes
      : printQuotationDetails.notes.split("\n").filter(n => n.trim())
    : quotationForPrintData?.notes
      ? Array.isArray(quotationForPrintData.notes)
        ? quotationForPrintData.notes
        : quotationForPrintData.notes.split("\n").filter(n => n.trim())
      : [];
  const quotationItems =
    printQuotationDetails?.items || quotationForPrintData?.items || [];
  const paymentTerms =
    printQuotationDetails?.payment_terms ||
    quotationForPrintData?.payment_terms ||
    "";
  const attentionTo =
    printQuotationDetails?.attention_to ||
    quotationForPrintData?.attention_to ||
    "";
  const subtotalAmount = Number(
    printQuotationDetails?.subtotal_amount ||
    quotationForPrintData?.subtotal_amount ||
    0,
  );
  const discountPercent = Number(
    printQuotationDetails?.discount_percent ||
    quotationForPrintData?.discount_percent ||
    0,
  );
  const vatPercent = Number(
    printQuotationDetails?.vat_percent ||
    quotationForPrintData?.vat_percent ||
    0,
  );
  const incomeTaxPercent = Number(
    printQuotationDetails?.income_tax_percent ||
    quotationForPrintData?.income_tax_percent ||
    0,
  );
  const totalAmount = Number(
    printQuotationDetails?.total_amount ||
    quotationForPrintData?.total_amount ||
    0,
  );

  const renderProjectName = quotation => {
    if (!quotation?.project) return "—";
    if (isRTL) {
      return (
        quotation.project.name_ar ||
        quotation.project.name_en ||
        quotation.project.code ||
        "—"
      );
    } else {
      return (
        quotation.project.name_en ||
        quotation.project.name_ar ||
        quotation.project.code ||
        "—"
      );
    }
  };

  const formatDate = date => (date ? String(date).split("T")[0] : "—");

  const formatCurrency = value =>
    value === undefined || value === null
      ? "—"
      : convertArabicNumbersToEnglish(
        Number(value).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );

  const renderStatus = status => {
    const meta = getStatusMeta(status);
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: 12,
          fontWeight: 600,
          color: meta.color,
          backgroundColor: meta.bg,
          whiteSpace: "nowrap",
        }}
      >
        {meta.label}
      </span>
    );
  };

  // Style definitions
  const labelStyle = {
    display: "block",
    marginBottom: 6,
    fontSize: 14,
    color: "white",
    direction: isRTL ? "rtl" : "ltr",
    textAlign: isRTL ? "right" : "left",
  };
  const dropdownToggleStyle = {
    width: "100%",
    padding: 10,
    backgroundColor: "#1a1f2e",
    border: "1px solid #333",
    borderRadius: 8,
    color: "white",
    fontSize: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  };
  const dropdownMenuStyle = {
    position: "absolute",
    zIndex: 10,
    top: "100%",
    left: 0,
    right: 0,
    background: "#1a1f2e",
    border: "1px solid #333",
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 400,
    overflowY: "auto",
  };
  const inputStyle = {
    width: "100%",
    padding: 8,
    backgroundColor: "#14263b",
    border: "1px solid #444",
    borderRadius: 6,
    color: "white",
    fontSize: 14,
  };
  const dropdownPaginationStyle = {
    padding: "8px",
    borderTop: "1px solid #333",
    background: "#14263b",
  };
  const dropdownPaginationInfo = {
    color: "#ccc",
    fontSize: 11,
    marginBottom: 6,
    textAlign: "center",
  };
  const dropdownPaginationControls = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 4,
    flexWrap: "wrap",
  };
  const dropdownItemStyle = {
    padding: 10,
    cursor: "pointer",
    borderTop: "1px solid #333",
    direction: isRTL ? "rtl" : "ltr",
    textAlign: isRTL ? "right" : "left",
    color: "white",
  };
  const actionButton = (bg, color = "#fff") => ({
    backgroundColor: bg,
    color: color,
    border: "none",
    borderRadius: 6,
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    gap: 6,
  });
  const headerStyle = {
    padding: 12,
    textAlign: "center",
    color: "white",
    fontWeight: 600,
    fontSize: 14,
    borderBottom: "1px solid rgba(255,255,255,0.1)",
  };
  const cellStyle = {
    padding: 12,
    color: "white",
    fontSize: 14,
    borderBottom: "1px solid #2c4a70",
    textAlign: "center",
  };
  const paginationButton = disabled => ({
    padding: "8px 12px",
    backgroundColor: disabled ? "#333" : COLORS.accent,
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: disabled ? "not-allowed" : "pointer",
  });

  const pageButton = active => ({
    padding: "8px 12px",
    backgroundColor: active ? COLORS.accent : "#1a1f2e",
    color: "white",
    border: "1px solid #333",
    borderRadius: "4px",
    cursor: "pointer",
  });

  const renderDropdownItem = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || "";
    const name = isRTL
      ? item[nameArField] || item[nameEnField] || ""
      : item[nameEnField] || item[nameArField] || "";
    return `${code ? `${code} - ` : ""}${name}`;
  };

  const renderProjectDisplay = () => {
    if (!filters.projectId) return t("quotations.filter.selectProject");
    const selected = (projects || []).find(
      p => String(p.id) === String(filters.projectId),
    );
    return selected
      ? renderDropdownItem(selected, "code", "name_ar", "name_en")
      : t("quotations.filter.selectProject");
  };

  return (
    <>
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          direction: dir,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <h1 style={{ margin: 0, fontSize: 20, textAlign }}>
            {t("quotations.title", { defaultValue: "Quotations" })}
          </h1>
          <div
            style={{
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <label
                style={{ color: "white", fontSize: 14, whiteSpace: "nowrap" }}
              >
                {t("common.perPage", { defaultValue: "Per Page" })}:
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button
                  onClick={() => {
                    const newValue = Math.max(1, perPageInput - 1);
                    setPerPageInput(newValue);
                  }}
                  style={{
                    backgroundColor: "#1b3350",
                    color: "white",
                    border: "1px solid #333",
                    padding: "6px 10px",
                    borderRadius: "6px 0 0 6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "32px",
                  }}
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max={filteredQuotations.length || 1000}
                  value={perPageInput}
                  onChange={e => {
                    const value = parseInt(e.target.value, 10) || 1;
                    const maxValue = filteredQuotations.length || 1000;
                    setPerPageInput(Math.max(1, Math.min(maxValue, value)));
                  }}
                  style={{
                    width: "70px",
                    padding: "6px 10px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderLeft: "none",
                    borderRight: "none",
                    borderRadius: 0,
                    color: "white",
                    fontSize: "14px",
                    textAlign: "center",
                  }}
                />
                <button
                  onClick={() => {
                    const maxValue = filteredQuotations.length || 1000;
                    const newValue = Math.min(maxValue, perPageInput + 1);
                    setPerPageInput(newValue);
                  }}
                  style={{
                    backgroundColor: "#1b3350",
                    color: "white",
                    border: "1px solid #333",
                    padding: "6px 10px",
                    borderRadius: "0 6px 6px 0",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "32px",
                  }}
                >
                  +
                </button>
              </div>
              <button
                onClick={handleApplyPerPage}
                style={{
                  backgroundColor: COLORS.accent,
                  color: "white",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("common.apply", { defaultValue: "تطبيق" })}
              </button>
            </div>
            <button
              onClick={() => navigate("/quotations/add")}
              style={{
                backgroundColor: COLORS.accent,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexDirection: isRTL ? "row" : "row-reverse",
              }}
            >
              <FaPlus />{" "}
              {t("quotations.button.addNew", {
                defaultValue: "Add New Quotation",
              })}
            </button>
          </div>
        </div>

        <div
          style={{
            background: "#1c2533",
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            border: "1px solid #2c4a70",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <div
              style={{ flex: "0 1 300px", position: "relative" }}
              data-project-dropdown
            >
              <label style={labelStyle}>
                {t("quotations.filter.project", { defaultValue: "المشروع" })}
              </label>
              <div
                onClick={() => {
                  const next = !projectDropdownOpen;
                  setProjectDropdownOpen(next);
                  if (!next) {
                    setProjectSearchTerm("");
                    setProjectPage(1);
                  }
                }}
                style={dropdownToggleStyle}
              >
                <span>
                  {filters.projectName || t("quotations.filter.selectProject")}
                </span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              {projectDropdownOpen && (
                <div
                  style={dropdownMenuStyle}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ padding: 8 }}>
                    <input
                      ref={projectSearchRef}
                      type="text"
                      placeholder={t("quotations.filter.searchProject")}
                      value={projectSearchTerm}
                      onChange={e => handleProjectSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={inputStyle}
                    />
                  </div>
                  {projectsPagination && projectsPagination.last_page >= 1 && (
                    <div style={dropdownPaginationStyle}>
                      <div style={dropdownPaginationInfo}>
                        {t("common.pagination.page", {
                          defaultValue: "الصفحة",
                        })}{" "}
                        {projectsPagination.current_page || 1}{" "}
                        {t("common.pagination.of", { defaultValue: "من" })}{" "}
                        {projectsPagination.last_page || 1}
                      </div>
                      <div style={dropdownPaginationControls}>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            if ((projectsPagination.current_page || 1) > 1) {
                              setProjectPage(1);
                            }
                          }}
                          disabled={
                            (projectsPagination.current_page || 1) === 1
                          }
                          style={{
                            padding: "4px 8px",
                            backgroundColor:
                              (projectsPagination.current_page || 1) === 1
                                ? "#2a2a2a"
                                : "#1a1f2e",
                            color:
                              (projectsPagination.current_page || 1) === 1
                                ? "#666"
                                : "white",
                            border: "1px solid #444",
                            borderRadius: "4px",
                            cursor:
                              (projectsPagination.current_page || 1) === 1
                                ? "not-allowed"
                                : "pointer",
                            fontSize: 10,
                            fontWeight: "bold",
                            minWidth: "28px",
                          }}
                          title={t("common.pagination.first", {
                            defaultValue: "الصفحة الأولى",
                          })}
                        >
                          ««
                        </button>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            if ((projectsPagination.current_page || 1) > 1) {
                              setProjectPage(prev => Math.max(1, prev - 1));
                            }
                          }}
                          disabled={
                            (projectsPagination.current_page || 1) === 1
                          }
                          style={{
                            padding: "4px 8px",
                            backgroundColor:
                              (projectsPagination.current_page || 1) === 1
                                ? "#2a2a2a"
                                : "#1a1f2e",
                            color:
                              (projectsPagination.current_page || 1) === 1
                                ? "#666"
                                : "white",
                            border: "1px solid #444",
                            borderRadius: "4px",
                            cursor:
                              (projectsPagination.current_page || 1) === 1
                                ? "not-allowed"
                                : "pointer",
                            fontSize: 10,
                            fontWeight: "bold",
                            minWidth: "28px",
                          }}
                          title={t("common.pagination.previous", {
                            defaultValue: "السابق",
                          })}
                        >
                          ‹
                        </button>
                        {(() => {
                          const pages = [];
                          const current = projectsPagination.current_page || 1;
                          const last = projectsPagination.last_page || 1;
                          const showPages = 5;
                          let start = Math.max(
                            1,
                            current - Math.floor(showPages / 2),
                          );
                          let end = Math.min(last, start + showPages - 1);
                          if (end - start < showPages - 1) {
                            start = Math.max(1, end - showPages + 1);
                          }
                          for (let i = start; i <= end; i++) {
                            pages.push(i);
                          }
                          return pages.map(page => (
                            <button
                              key={page}
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                setProjectPage(page);
                              }}
                              style={{
                                padding: "4px 8px",
                                backgroundColor:
                                  page === current ? "#AC2000" : "#1a1f2e",
                                color: "white",
                                border:
                                  page === current
                                    ? "1px solid #AC2000"
                                    : "1px solid #444",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: 10,
                                fontWeight:
                                  page === current ? "bold" : "normal",
                                minWidth: "28px",
                              }}
                            >
                              {page}
                            </button>
                          ));
                        })()}
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            if (
                              (projectsPagination.current_page || 1) <
                              (projectsPagination.last_page || 1)
                            ) {
                              setProjectPage(prev => prev + 1);
                            }
                          }}
                          disabled={
                            (projectsPagination.current_page || 1) ===
                            (projectsPagination.last_page || 1)
                          }
                          style={{
                            padding: "4px 8px",
                            backgroundColor:
                              (projectsPagination.current_page || 1) ===
                                (projectsPagination.last_page || 1)
                                ? "#2a2a2a"
                                : "#1a1f2e",
                            color:
                              (projectsPagination.current_page || 1) ===
                                (projectsPagination.last_page || 1)
                                ? "#666"
                                : "white",
                            border: "1px solid #444",
                            borderRadius: "4px",
                            cursor:
                              (projectsPagination.current_page || 1) ===
                                (projectsPagination.last_page || 1)
                                ? "not-allowed"
                                : "pointer",
                            fontSize: 10,
                            fontWeight: "bold",
                            minWidth: "28px",
                          }}
                          title={t("common.pagination.next", {
                            defaultValue: "التالي",
                          })}
                        >
                          ›
                        </button>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            const last = projectsPagination.last_page || 1;
                            if ((projectsPagination.current_page || 1) < last) {
                              setProjectPage(last);
                            }
                          }}
                          disabled={
                            (projectsPagination.current_page || 1) ===
                            (projectsPagination.last_page || 1)
                          }
                          style={{
                            padding: "4px 8px",
                            backgroundColor:
                              (projectsPagination.current_page || 1) ===
                                (projectsPagination.last_page || 1)
                                ? "#2a2a2a"
                                : "#1a1f2e",
                            color:
                              (projectsPagination.current_page || 1) ===
                                (projectsPagination.last_page || 1)
                                ? "#666"
                                : "white",
                            border: "1px solid #444",
                            borderRadius: "4px",
                            cursor:
                              (projectsPagination.current_page || 1) ===
                                (projectsPagination.last_page || 1)
                                ? "not-allowed"
                                : "pointer",
                            fontSize: 10,
                            fontWeight: "bold",
                            minWidth: "28px",
                          }}
                          title={t("common.pagination.last", {
                            defaultValue: "الصفحة الأخيرة",
                          })}
                        >
                          »»
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{ maxHeight: 220, overflowY: "auto" }}>
                    {(projects || []).map(project => (
                      <div
                        key={project.id}
                        onClick={e => {
                          e.stopPropagation();
                          setFilters(prev => ({
                            ...prev,
                            projectId: project.id,
                            projectName:
                              project.name_ar ||
                              project.name_en ||
                              project.code ||
                              "مشروع",
                          }));
                          setProjectDropdownOpen(false);
                          setProjectSearchTerm("");
                          setProjectPage(1);
                          setCurrentPage(1);
                        }}
                        style={{
                          ...dropdownItemStyle,
                          direction: isRTL ? "rtl" : "ltr",
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {renderDropdownItem(
                          project,
                          "code",
                          "name_ar",
                          "name_en",
                        )}
                      </div>
                    ))}
                    {(projects || []).length === 0 && (
                      <div
                        style={{
                          padding: 12,
                          textAlign: "center",
                          color: COLORS.neutral,
                        }}
                      >
                        {t("quotations.filter.noResults", {
                          defaultValue: "لا يوجد نتائج",
                        })}
                      </div>
                    )}
                  </div>
                  {filters.projectId && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        resetFilters();
                      }}
                      style={{
                        ...actionButton("#2f3644"),
                        width: "100%",
                        marginTop: 10,
                      }}
                    >
                      {t("quotations.filter.clearSelection", {
                        defaultValue: "إزالة التحديد",
                      })}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Client Label Filter */}
            <div style={{ flex: "0 1 200px" }}>
              <label style={labelStyle}>
                {t("quotations.filter.clientType", {
                  defaultValue: "نوع العميل",
                })}
              </label>
              <select
                value={filters.clientLabel || ""}
                onChange={e => {
                  const nextLabel = e.target.value || null;
                  setFilters(prev => ({
                    ...prev,
                    clientLabel: nextLabel,
                    customerId: null,
                    customerName: "",
                  }));
                  setCustomerSearchTerm("");
                  setCustomerPage(1);
                  setCurrentPage(1);
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#1a1f2e",
                  border: "1px solid #333",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  cursor: "pointer",
                  boxSizing: "border-box",
                  direction: isRTL ? "rtl" : "ltr",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                <option value="">
                  {t("quotations.filter.all", { defaultValue: "الكل" })}
                </option>
                <option value="customer">
                  {t("quotations.filter.customer", { defaultValue: "عميل" })}
                </option>
                <option value="contractor">
                  {t("quotations.filter.contractor", { defaultValue: "مقاول" })}
                </option>
              </select>
            </div>

            {/* Customer Filter */}
            <div
              style={{ flex: "0 1 300px", position: "relative" }}
              data-customer-dropdown
            >
              <label style={labelStyle}>
                {filters.clientLabel === "contractor"
                  ? t("quotations.filter.contractor", {
                    defaultValue: "المقاول",
                  })
                  : t("quotations.filter.customer", { defaultValue: "العميل" })}
              </label>
              <div
                onClick={() => {
                  const next = !customerDropdownOpen;
                  setCustomerDropdownOpen(next);
                  if (!next) {
                    setCustomerSearchTerm("");
                    setCustomerPage(1);
                  }
                }}
                style={dropdownToggleStyle}
              >
                <span>
                  {filters.customerName ||
                    (filters.clientLabel === "contractor"
                      ? t("quotations.filter.selectContractor")
                      : t("quotations.filter.selectCustomer"))}
                </span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              {customerDropdownOpen && (
                <div
                  style={dropdownMenuStyle}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ padding: 8 }}>
                    <input
                      ref={customerSearchRef}
                      type="text"
                      placeholder={
                        filters.clientLabel === "contractor"
                          ? t("quotations.filter.searchContractor")
                          : t("quotations.filter.searchCustomer")
                      }
                      value={customerSearchTerm}
                      onChange={e => handleCustomerSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={inputStyle}
                    />
                  </div>
                  {filters.clientLabel !== "contractor" &&
                    customersPagination &&
                    customersPagination.last_page >= 1 && (
                      <div style={dropdownPaginationStyle}>
                        <div style={dropdownPaginationInfo}>
                          {t("common.pagination.page", {
                            defaultValue: "الصفحة",
                          })}{" "}
                          {customersPagination.current_page || 1}{" "}
                          {t("common.pagination.of", { defaultValue: "من" })}{" "}
                          {customersPagination.last_page || 1}
                        </div>
                        <div style={dropdownPaginationControls}>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              if ((customersPagination.current_page || 1) > 1) {
                                setCustomerPage(1);
                              }
                            }}
                            disabled={
                              (customersPagination.current_page || 1) === 1
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                (customersPagination.current_page || 1) === 1
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                (customersPagination.current_page || 1) === 1
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                (customersPagination.current_page || 1) === 1
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ««
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              if ((customersPagination.current_page || 1) > 1) {
                                setCustomerPage(prev => Math.max(1, prev - 1));
                              }
                            }}
                            disabled={
                              (customersPagination.current_page || 1) === 1
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                (customersPagination.current_page || 1) === 1
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                (customersPagination.current_page || 1) === 1
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                (customersPagination.current_page || 1) === 1
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ‹
                          </button>
                          {(() => {
                            const pages = [];
                            const current =
                              customersPagination.current_page || 1;
                            const last = customersPagination.last_page || 1;
                            const showPages = 5;
                            let start = Math.max(
                              1,
                              current - Math.floor(showPages / 2),
                            );
                            let end = Math.min(last, start + showPages - 1);
                            if (end - start < showPages - 1) {
                              start = Math.max(1, end - showPages + 1);
                            }
                            for (let i = start; i <= end; i++) {
                              pages.push(i);
                            }
                            return pages.map(page => (
                              <button
                                key={page}
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  setCustomerPage(page);
                                }}
                                style={{
                                  padding: "4px 8px",
                                  backgroundColor:
                                    page === current ? "#AC2000" : "#1a1f2e",
                                  color: "white",
                                  border:
                                    page === current
                                      ? "1px solid #AC2000"
                                      : "1px solid #444",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: 10,
                                  fontWeight:
                                    page === current ? "bold" : "normal",
                                  minWidth: "28px",
                                }}
                              >
                                {page}
                              </button>
                            ));
                          })()}
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              if (
                                (customersPagination.current_page || 1) <
                                (customersPagination.last_page || 1)
                              ) {
                                setCustomerPage(prev => prev + 1);
                              }
                            }}
                            disabled={
                              (customersPagination.current_page || 1) ===
                              (customersPagination.last_page || 1)
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                (customersPagination.current_page || 1) ===
                                  (customersPagination.last_page || 1)
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                (customersPagination.current_page || 1) ===
                                  (customersPagination.last_page || 1)
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                (customersPagination.current_page || 1) ===
                                  (customersPagination.last_page || 1)
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              const last = customersPagination.last_page || 1;
                              if (
                                (customersPagination.current_page || 1) < last
                              ) {
                                setCustomerPage(last);
                              }
                            }}
                            disabled={
                              (customersPagination.current_page || 1) ===
                              (customersPagination.last_page || 1)
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                (customersPagination.current_page || 1) ===
                                  (customersPagination.last_page || 1)
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                (customersPagination.current_page || 1) ===
                                  (customersPagination.last_page || 1)
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                (customersPagination.current_page || 1) ===
                                  (customersPagination.last_page || 1)
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                          >
                            »»
                          </button>
                        </div>
                      </div>
                    )}
                  <div style={{ maxHeight: 220, overflowY: "auto" }}>
                    {clientFilterOptions.map(customer => (
                      <div
                        key={customer.id}
                        onClick={e => {
                          e.stopPropagation();
                          setFilters(prev => ({
                            ...prev,
                            customerId: customer.id,
                            customerName:
                              customer.name_ar ||
                              customer.name ||
                              customer.name_en ||
                              "عميل",
                          }));
                          setCustomerDropdownOpen(false);
                          setCustomerSearchTerm("");
                          setCustomerPage(1);
                          setCurrentPage(1);
                        }}
                        style={dropdownItemStyle}
                      >
                        {renderDropdownItem(
                          customer,
                          "code",
                          "name_ar",
                          "name",
                        )}
                      </div>
                    ))}
                    {clientFilterOptions.length === 0 && (
                      <div
                        style={{
                          padding: 12,
                          textAlign: "center",
                          color: COLORS.neutral,
                        }}
                      >
                        {t("quotations.filter.noResults", {
                          defaultValue: "لا يوجد نتائج",
                        })}
                      </div>
                    )}
                  </div>
                  {filters.customerId && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setFilters(prev => ({
                          ...prev,
                          customerId: null,
                          customerName: "",
                        }));
                        setCurrentPage(1);
                      }}
                      style={{
                        ...actionButton("#2f3644"),
                        width: "100%",
                        marginTop: 10,
                      }}
                    >
                      {t("quotations.filter.clearSelection", {
                        defaultValue: "إزالة التحديد",
                      })}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-end",
                flexWrap: "nowrap",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <button
                onClick={handlePreviewAll}
                style={{
                  backgroundColor: "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  minWidth: "140px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                <FaEye /> {t("quotations.button.previewAllPdf")}
              </button>
              <button
                onClick={handlePrintAll}
                disabled={isGeneratingPdfAll}
                style={{
                  backgroundColor: isGeneratingPdfAll ? "#666" : "#17a2b8",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: isGeneratingPdfAll ? "not-allowed" : "pointer",
                  minWidth: "140px",
                  opacity: isGeneratingPdfAll ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                <FaPrint />{" "}
                {isGeneratingPdfAll
                  ? t("quotations.print.generating")
                  : t("quotations.button.printAll")}
              </button>
              <button
                onClick={handleExportAllToExcel}
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                <FaFileExcel />{" "}
                {t("quotations.button.exportAll", {
                  defaultValue: "تصدير Excel للكل",
                })}
              </button>
              <button
                onClick={resetFilters}
                style={{
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  minWidth: "140px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("btns.reset", { defaultValue: "إعادة التعيين" })}
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            background: "#1b3350",
            borderRadius: 12,
            border: "1px solid #2c4a70",
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                minWidth: 1024,
                borderCollapse: "separate",
                borderSpacing: 0,
              }}
            >
              <thead>
                <tr style={{ backgroundColor: COLORS.accent }}>
                  <th style={{ ...headerStyle, textAlign: "center", width: "50px" }}>
                    #
                  </th>
                  <th style={{ ...headerStyle, textAlign: "center" }}>
                    {t("quotations.tableHeaders.quotationNumber", {
                      defaultValue: "رقم العرض",
                    })}
                  </th>
                  <th style={{ ...headerStyle, textAlign: "center" }}>
                    {t("quotations.tableHeaders.project", {
                      defaultValue: "المشروع",
                    })}
                  </th>
                  <th style={{ ...headerStyle, textAlign: "center" }}>
                    {filters.clientLabel === "contractor"
                      ? t("quotations.tableHeaders.contractor", {
                        defaultValue: "المقاول",
                      })
                      : filters.clientLabel === "customer"
                        ? t("quotations.tableHeaders.customer", {
                          defaultValue: "العميل",
                        })
                        : t("quotations.tableHeaders.customer", {
                          defaultValue: "العميل",
                        })}
                  </th>
                  <th style={{ ...headerStyle, textAlign: "center" }}>
                    {t("quotations.tableHeaders.itemsCount", {
                      defaultValue: "عدد البنود",
                    })}
                  </th>
                  <th style={{ ...headerStyle, textAlign: "center" }}>
                    {t("quotations.tableHeaders.total", {
                      defaultValue: "الإجمالي",
                    })}
                  </th>
                  <th style={{ ...headerStyle, textAlign: "center" }}>
                    {t("quotations.tableHeaders.status", {
                      defaultValue: "الحالة",
                    })}
                  </th>
                  <th style={{ ...headerStyle, textAlign: "center" }}>
                    {t("quotations.tableHeaders.createdAt", {
                      defaultValue: "تاريخ الإنشاء",
                    })}
                  </th>
                  <th style={{ ...headerStyle, textAlign: "center" }}>
                    {t("quotations.tableHeaders.sentAt", {
                      defaultValue: "تاريخ الإرسال",
                    })}
                  </th>
                  <th style={{ ...headerStyle, textAlign: "center" }}>
                    {t("quotations.tableHeaders.actions", {
                      defaultValue: "الإجراءات",
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isTableLoading ? (
                  <tr>
                    <td
                      colSpan="10"
                      style={{
                        textAlign: "center",
                        padding: 24,
                        color: "#fff",
                      }}
                    >
                      {t("quotations.loading", {
                        defaultValue: "جاري تحميل البيانات...",
                      })}
                    </td>
                  </tr>
                ) : paginatedQuotations.length === 0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      style={{
                        textAlign: "center",
                        padding: 20,
                        color: COLORS.neutral,
                        direction: dir,
                      }}
                    >
                      {t("quotations.noData", {
                        defaultValue: "No data available",
                      })}
                    </td>
                  </tr>
                ) : (
                  paginatedQuotations.map((quotation, index) => {
                    const customerName =
                      quotation.customer_name ||
                      (quotation.client_label === "contractor"
                        ? isRTL
                          ? quotation.contractor?.name_ar ||
                          quotation.contractor?.name_en
                          : quotation.contractor?.name_en ||
                          quotation.contractor?.name_ar
                        : quotation.client_label === "customer"
                          ? isRTL
                            ? quotation.customer?.name_ar ||
                            quotation.customer?.name_en
                            : quotation.customer?.name_en ||
                            quotation.customer?.name_ar
                          : isRTL
                            ? quotation.contractor?.name_ar ||
                            quotation.contractor?.name_en ||
                            "—"
                            : quotation.contractor?.name_en ||
                            quotation.contractor?.name_ar ||
                            "—");
                    return (
                      <tr
                        key={quotation.id}
                        style={{ borderBottom: "1px solid #2c4a70" }}
                      >
                        <td
                          style={{
                            ...cellStyle,
                            whiteSpace: "nowrap",
                            minWidth: 50,
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {(currentPage - 1) * perPage + index + 1}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            whiteSpace: "nowrap",
                            minWidth: 120,
                            textAlign: "center",
                          }}
                        >
                          {quotation.quotation_no ||
                            quotation.quotation_number ||
                            quotation.id ||
                            "—"}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            whiteSpace: "nowrap",
                            minWidth: 200,
                            textAlign: "center",
                            direction: dir,
                          }}
                        >
                          {renderProjectName(quotation)}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            whiteSpace: "nowrap",
                            minWidth: 150,
                            textAlign: "center",
                            direction: dir,
                          }}
                        >
                          {customerName || "—"}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            whiteSpace: "nowrap",
                            minWidth: 120,
                            textAlign: "center",
                          }}
                        >
                          {quotation.items?.length || 0}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            whiteSpace: "nowrap",
                            minWidth: 120,
                            textAlign: "center",
                          }}
                        >
                          {formatCurrency(
                            quotation.total_amount ||
                            quotation.subtotal_amount ||
                            quotation.subtotal,
                          )}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            whiteSpace: "nowrap",
                            minWidth: 120,
                            textAlign: "center",
                          }}
                        >
                          {renderStatus(quotation.status)}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            whiteSpace: "nowrap",
                            minWidth: 120,
                            textAlign: "center",
                          }}
                        >
                          {formatDate(quotation.created_at)}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            whiteSpace: "nowrap",
                            minWidth: 120,
                            textAlign: "center",
                          }}
                        >
                          {formatDate(quotation.sent_at)}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                            minWidth: "200px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              flexWrap: "nowrap",
                              justifyContent: "center",
                              flexDirection: isRTL ? "row-reverse" : "row",
                            }}
                          >
                            <ActionButton
                              title={t("quotations.button.previewPdf")}
                              onClick={() => handlePreviewQuotation(quotation)}
                            >
                              <FaEye />
                            </ActionButton>
                            <ActionButton
                              title={t("quotations.button.print")}
                              bg="#1f5ff1"
                              onClick={() => handlePrintQuotation(quotation)}
                            >
                              <FaPrint />
                            </ActionButton>
                            <ActionButton
                              title={t("quotations.button.export", {
                                defaultValue: "تصدير Excel",
                              })}
                              bg="#28a745"
                              onClick={() =>
                                handleExportQuotationToExcel(quotation)
                              }
                            >
                              <FaFileExcel />
                            </ActionButton>
                            <ActionButton
                              title={t("btns.edit")}
                              onClick={() =>
                                navigate(`/quotations/edit/${quotation.id}`)
                              }
                            >
                              <FaEdit />
                            </ActionButton>
                            {(quotation.status === "sent_to_client" ||
                              quotation.status === "draft") && (
                                <ActionButton
                                  title={t("quotations.button.approve", {
                                    defaultValue: "اعتماد",
                                  })}
                                  bg="#27ae60"
                                  onClick={() => handleApproveItems(quotation)}
                                >
                                  <FaCheck />
                                </ActionButton>
                              )}
                            <ActionButton
                              title={t("btns.delete")}
                              bg="#c0392b"
                              onClick={() => setQuotationToDelete(quotation)}
                            >
                              <FaTrash />
                            </ActionButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                marginTop: "20px",
                padding: "16px",
                borderTop: "1px solid #2c4a70",
                flexDirection: "row",
                direction: isRTL ? "rtl" : "ltr",
              }}
            >
              {isRTL ? (
                <>
                  <button
                    onClick={() => setCurrentPage(currentPageSafe - 1)}
                    disabled={currentPageSafe === 1}
                    style={paginationButton(currentPageSafe === 1)}
                  >
                    {t("common.previous", { defaultValue: "Previous" })}
                  </button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let page;
                    if (totalPages <= 10) {
                      page = i + 1;
                    } else if (currentPageSafe <= 5) {
                      page = i + 1;
                    } else if (currentPageSafe >= totalPages - 4) {
                      page = totalPages - 9 + i;
                    } else {
                      page = currentPageSafe - 4 + i;
                    }
                    return page;
                  }).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={pageButton(page === currentPageSafe)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(currentPageSafe + 1)}
                    disabled={currentPageSafe === totalPages}
                    style={paginationButton(currentPageSafe === totalPages)}
                  >
                    {t("common.next", { defaultValue: "Next" })}
                  </button>
                  <span
                    style={{
                      color: "#9ca3af",
                      fontSize: "14px",
                      marginRight: "10px",
                      alignSelf: "center",
                      direction: "rtl",
                    }}
                  >
                    {t("common.pageInfo", {
                      defaultValue: "Page {{current}} of {{total}}",
                      current: currentPageSafe,
                      total: totalPages,
                    })}
                  </span>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setCurrentPage(currentPageSafe - 1)}
                    disabled={currentPageSafe === 1}
                    style={paginationButton(currentPageSafe === 1)}
                  >
                    {t("common.previous", { defaultValue: "Previous" })}
                  </button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let page;
                    if (totalPages <= 10) {
                      page = i + 1;
                    } else if (currentPageSafe <= 5) {
                      page = i + 1;
                    } else if (currentPageSafe >= totalPages - 4) {
                      page = totalPages - 9 + i;
                    } else {
                      page = currentPageSafe - 4 + i;
                    }
                    return page;
                  }).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={pageButton(page === currentPageSafe)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(currentPageSafe + 1)}
                    disabled={currentPageSafe === totalPages}
                    style={paginationButton(currentPageSafe === totalPages)}
                  >
                    {t("common.next", { defaultValue: "Next" })}
                  </button>
                  <span
                    style={{
                      color: "#9ca3af",
                      fontSize: "14px",
                      marginLeft: "10px",
                      alignSelf: "center",
                      direction: "ltr",
                    }}
                  >
                    {t("common.pageInfo", {
                      defaultValue: "Page {{current}} of {{total}}",
                      current: currentPageSafe,
                      total: totalPages,
                    })}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {quotationToDelete &&
          ReactDOM.createPortal(
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.7)",
                zIndex: 999999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  background: "#1b3350",
                  borderRadius: 12,
                  padding: 24,
                  minWidth: 400,
                  maxWidth: 500,
                  border: "1px solid #333",
                }}
                onClick={e => e.stopPropagation()}
              >
                <h3 style={{ marginBottom: 12, color: "white" }}>
                  {t("quotations.delete.title", {
                    defaultValue: "حذف عرض السعر",
                  })}
                </h3>
                <p style={{ color: COLORS.neutral, marginBottom: 8 }}>
                  {t("quotations.delete.message", {
                    defaultValue: "هل أنت متأكد من حذف هذا عرض السعر؟",
                  })}
                </p>
                <p
                  style={{ fontWeight: 600, marginBottom: 18, color: "white" }}
                >
                  {quotationToDelete.quotation_number ||
                    quotationToDelete.quotation_no ||
                    quotationToDelete.id}
                </p>
                <p style={{ color: "#c0392b", fontSize: 13, marginBottom: 20 }}>
                  {t("quotations.delete.warning", {
                    defaultValue: "لا يمكن التراجع عن هذه العملية",
                  })}
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => setQuotationToDelete(null)}
                    style={actionButton("transparent", "#fff")}
                  >
                    {t("btns.cancel")}
                  </button>
                  <button
                    onClick={handleDelete}
                    style={actionButton("#c0392b")}
                  >
                    {t("quotations.delete.confirm", {
                      defaultValue: "تأكيد الحذف",
                    })}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}

        {quotationToApprove &&
          ReactDOM.createPortal(
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.7)",
                zIndex: 999999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  background: "#1b3350",
                  borderRadius: 12,
                  padding: 24,
                  minWidth: 600,
                  maxWidth: 800,
                  maxHeight: "80vh",
                  overflowY: "auto",
                  border: "1px solid #333",
                  direction: isRTL ? "rtl" : "ltr",
                }}
                onClick={e => e.stopPropagation()}
              >
                <h3
                  style={{
                    marginBottom: 16,
                    color: "white",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t("quotations.approve.title", {
                    defaultValue: "اعتماد بنود عرض السعر",
                  })}
                </h3>
                <p
                  style={{
                    color: COLORS.neutral,
                    marginBottom: 12,
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t("quotations.approve.quotation", {
                    defaultValue: "عرض السعر",
                  })}
                  :{" "}
                  <strong style={{ color: "white" }}>
                    {quotationToApprove.quotation_number ||
                      quotationToApprove.quotation_no ||
                      quotationToApprove.id}
                  </strong>
                </p>
                <p
                  style={{
                    color: COLORS.neutral,
                    marginBottom: 20,
                    fontSize: 14,
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {t("quotations.message.selectApprovedItems")}
                </p>

                {quotationToApprove.items &&
                  quotationToApprove.items.length > 0 ? (
                  <div style={{ marginBottom: 20 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 12,
                        padding: "8px 0",
                        borderBottom: "1px solid #333",
                      }}
                    >
                      <div style={{ flex: "0 0 40px" }}>
                        <input
                          type="checkbox"
                          checked={
                            selectedItemIds.length ===
                            quotationToApprove.items.length
                          }
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedItemIds(
                                quotationToApprove.items.map(item => item.id),
                              );
                            } else {
                              setSelectedItemIds([]);
                            }
                          }}
                          style={{ width: 18, height: 18, cursor: "pointer" }}
                        />
                      </div>
                      <div
                        style={{
                          flex: "1",
                          fontWeight: 600,
                          color: "white",
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {t("quotations.approve.selectAll", {
                          defaultValue: "تحديد الكل",
                        })}
                      </div>
                    </div>
                    {quotationToApprove.items.map(item => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          gap: 12,
                          padding: 12,
                          marginBottom: 8,
                          background: selectedItemIds.includes(item.id)
                            ? "rgba(172, 32, 0, 0.2)"
                            : "#1a1f2e",
                          borderRadius: 6,
                          border: selectedItemIds.includes(item.id)
                            ? "1px solid #AC2000"
                            : "1px solid #333",
                          cursor: "pointer",
                          direction: isRTL ? "rtl" : "ltr",
                        }}
                        onClick={() => handleToggleItem(item.id)}
                      >
                        <div
                          style={{
                            flex: "0 0 40px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedItemIds.includes(item.id)}
                            onChange={() => handleToggleItem(item.id)}
                            onClick={e => e.stopPropagation()}
                            style={{ width: 18, height: 18, cursor: "pointer" }}
                          />
                        </div>
                        <div
                          style={{
                            flex: "1",
                            direction: isRTL ? "rtl" : "ltr",
                          }}
                        >
                          <div
                            style={{
                              color: "white",
                              marginBottom: 4,
                              fontWeight: 600,
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {isRTL
                              ? item.description_ar || item.description || "—"
                              : item.description_en ||
                              item.description_ar ||
                              item.description ||
                              "—"}
                          </div>
                          {!isRTL && item.description_ar && (
                            <div
                              style={{
                                color: COLORS.neutral,
                                fontSize: 12,
                                marginBottom: 4,
                                textAlign: "right",
                              }}
                            >
                              {item.description_ar}
                            </div>
                          )}
                          {isRTL && item.description_en && (
                            <div
                              style={{
                                color: COLORS.neutral,
                                fontSize: 12,
                                marginBottom: 4,
                                textAlign: "left",
                              }}
                            >
                              {item.description_en}
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              gap: 12,
                              fontSize: 12,
                              color: COLORS.neutral,
                              marginTop: 4,
                              flexWrap: "wrap",
                              direction: isRTL ? "rtl" : "ltr",
                            }}
                          >
                            {item.unit && (
                              <span>
                                {t("quotations.approve.unit", {
                                  defaultValue: "الوحدة",
                                })}
                                :{" "}
                                {typeof item.unit === "object" &&
                                  item.unit !== null
                                  ? item.unit.symbol
                                    ? `${isRTL ? item.unit.name_ar || item.unit.name_en || "" : item.unit.name_en || item.unit.name_ar || ""} (${item.unit.symbol})`
                                    : isRTL
                                      ? item.unit.name_ar ||
                                      item.unit.name_en ||
                                      item.unit.code ||
                                      String(item.unit)
                                      : item.unit.name_en ||
                                      item.unit.name_ar ||
                                      item.unit.code ||
                                      String(item.unit)
                                  : String(item.unit)}
                              </span>
                            )}
                            {item.qty && (
                              <span>
                                {t("quotations.approve.quantity", {
                                  defaultValue: "الكمية",
                                })}
                                : {item.qty}
                              </span>
                            )}
                            {item.unit_price && (
                              <span>
                                {t("quotations.approve.price", {
                                  defaultValue: "السعر",
                                })}
                                :{" "}
                                {Number(item.unit_price).toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}
                              </span>
                            )}
                            {item.qty && item.unit_price && (
                              <span
                                style={{
                                  color: COLORS.secondary,
                                  fontWeight: 600,
                                }}
                              >
                                {t("quotations.approve.total", {
                                  defaultValue: "الإجمالي",
                                })}
                                :{" "}
                                {(
                                  Number(item.qty) * Number(item.unit_price)
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p
                    style={{
                      color: COLORS.neutral,
                      textAlign: "center",
                      padding: 20,
                    }}
                  >
                    {t("quotations.approve.noItems", {
                      defaultValue: "لا توجد بنود لعرضها",
                    })}
                  </p>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: isRTL ? "flex-start" : "flex-end",
                    marginTop: 20,
                    paddingTop: 20,
                    borderTop: "1px solid #333",
                    flexDirection: isRTL ? "row-reverse" : "row",
                  }}
                >
                  <button
                    onClick={() => {
                      setQuotationToApprove(null);
                      setSelectedItemIds([]);
                    }}
                    style={actionButton("transparent", "#fff")}
                  >
                    {t("btns.cancel")}
                  </button>
                  <button
                    onClick={handleConfirmApprove}
                    disabled={selectedItemIds.length === 0}
                    style={{
                      ...actionButton("#27ae60"),
                      opacity: selectedItemIds.length === 0 ? 0.5 : 1,
                      cursor:
                        selectedItemIds.length === 0
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    <FaCheck />{" "}
                    {t("quotations.approve.confirm", {
                      defaultValue: "اعتماد",
                    })}{" "}
                    ({selectedItemIds.length})
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}

        {/* Hidden PDF Template — QuotationsPrintTemplate is the single source of truth.
            page1Id and page2Id make the page divs discoverable by handlePrintQuotation. */}
        {quotationForPrintData && (
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0 }}>
            <QuotationsPrintTemplate
              quotationForPrintData={quotationForPrintData}
              printQuotationDetails={printQuotationDetails}
              quotationDate={quotationDate}
              contractorName={contractorName}
              clientLabelText={clientLabelText}
              quotationSubject={quotationSubject}
              quotationProject={quotationProject}
              quotationLocation={quotationLocation}
              quotationRevision={quotationRevision}
              quotationNotes={quotationNotes}
              quotationItems={quotationItems}
              paymentTerms={paymentTerms}
              attentionTo={attentionTo}
              subtotalAmount={subtotalAmount}
              discountPercent={discountPercent}
              vatPercent={vatPercent}
              incomeTaxPercent={incomeTaxPercent}
              totalAmount={totalAmount}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              isRTL={isRTL}
              t={t}
              page1Id="quotation-pdf"
              page2Id="quotation-pdf-page2"
            />
          </div>
        )}


        {/* ══════════════════════════════════════════════════════════════════
            PDF MEASUREMENT TEMPLATE
            Always rendered off-screen so calculatePdfPagination can measure
            actual DOM heights at any time without waiting for pdfType state.
        ══════════════════════════════════════════════════════════════════ */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', left: '-9999px', top: 0,
            width: '794px', backgroundColor: '#fff',
            padding: '24px', boxSizing: 'border-box',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          <div style={{ border: '2px solid #000', padding: '18px', display: 'flex', flexDirection: 'column' }}>
            <div id="quotations-pdf-measure-top-header" style={{ flexShrink: 0 }}>
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt=""
                style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }}
                crossOrigin="anonymous"
              />
              <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0 }}>
                  {t('quotations.pdf.reportTitle', { defaultValue: isRTL ? 'تقرير عروض الأسعار' : 'Quotations Report' })}
                </h2>
                <p style={{ fontSize: '12px', color: '#333', margin: '4px 0 0' }}>
                  {new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                </p>
                {activeFiltersForPdf && (
                  <div style={{ fontSize: '11px', color: '#444', margin: '4px 0 0', padding: '4px 8px', backgroundColor: 'rgba(0,0,128,0.05)', borderRadius: '4px', display: 'inline-block' }}>
                    {activeFiltersForPdf}
                  </div>
                )}
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
              <thead>
                <tr id="quotations-pdf-measure-thead">
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '36px' }}>#</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px' }}>{t('quotations.tableHeaders.quotationNumber')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', width: '120px' }}>{t('quotations.tableHeaders.project')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', width: '120px' }}>{t('quotations.tableHeaders.customer')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px' }}>{t('quotations.tableHeaders.itemsCount')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '90px' }}>{t('quotations.tableHeaders.total')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px' }}>{t('quotations.tableHeaders.status')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '90px' }}>{t('quotations.tableHeaders.createdAt')}</th>
                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '90px' }}>{t('quotations.tableHeaders.sentAt')}</th>
                </tr>
              </thead>
              <tbody id="quotations-pdf-measure-tbody">
                <tr>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>1</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>Q-001</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'left' }}>Project Alpha</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'left' }}>Customer Alpha</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>5</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>$1,000.00</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>Pending</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>01/01/2026</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>01/01/2026</td>
                </tr>
              </tbody>
            </table>
            <div id="quotations-pdf-measure-footer" style={{ height: '120px', flexShrink: 0 }} />
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            HIDDEN PDF PRINT TEMPLATES
        ══════════════════════════════════════════════════════════════════ */}
        {pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`quotations-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '794px',
              height: '1123px',
              backgroundColor: '#ffffff',
              color: '#000000',
              padding: '24px',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
          >
            <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
              <PdfPageInner
                chunk={chunk.items}
                startIdx={chunk.startIndex}
                chunkIndex={chunkIndex}
                totalChunks={pdfChunks.length}
                showDate={true}
                isRTL={isRTL}
                t={t}
                activeFiltersForPdf={activeFiltersForPdf}
                headerLogo={headerLogo}
                publicUrl={publicUrl}
                dir={dir}
                textAlign={textAlign}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                renderStatusText={renderStatusText}
              />
            </div>
          </div>
        ))}

        {/* PDF Preview Modal */}
        <PdfPreviewModal
          isOpen={isPreviewOpen}
          title={
            pdfType === "all" || previewQuotation?.all
              ? t("quotations.pdf.reportTitle", { defaultValue: "Quotations Report" })
              : t("quotations.preview.single", { defaultValue: "معاينة عرض السعر" })
          }
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewQuotation(null);
            setPdfType(null);
            setPdfChunks([]);
          }}
          onPrint={
            pdfType === "all" || previewQuotation?.all
              ? handlePrintAll
              : handlePrintFromPreview
          }
          isGeneratingPdf={isGeneratingQuotation || isGeneratingPdfAll}
        >
          {pdfType === "all" || previewQuotation?.all ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px", alignItems: "center" }}>
              {pdfChunks.map((chunk, chunkIndex) => (
                <div
                  key={chunkIndex}
                  id={`preview-quotations-pdf-all-${chunkIndex}`}
                  style={{
                    width: "794px",
                    height: "1123px",
                    backgroundColor: "#ffffff",
                    color: "#000000",
                    padding: "24px",
                    boxSizing: "border-box",
                    direction: isRTL ? "rtl" : "ltr",
                    transform: "scale(1.0)",
                    transformOrigin: "top center",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                    position: "relative"
                  }}
                >
                  <div style={{ border: "5px solid #000", padding: "3px", backgroundColor: "#ffffff", boxSizing: "border-box", width: "100%", height: "1043px", overflow: "hidden" }}>
                    <PdfPageInner
                      chunk={chunk.items}
                      startIdx={chunk.startIndex}
                      chunkIndex={chunkIndex}
                      totalChunks={pdfChunks.length}
                      showDate={true}
                      isRTL={isRTL}
                      t={t}
                      activeFiltersForPdf={activeFiltersForPdf}
                      headerLogo={headerLogo}
                      publicUrl={publicUrl}
                      dir={dir}
                      textAlign={textAlign}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                      renderStatusText={renderStatusText}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            quotationForPrintData && (
              <QuotationsPrintTemplate
                quotationForPrintData={quotationForPrintData}
                printQuotationDetails={printQuotationDetails}
                quotationDate={quotationDate}
                contractorName={contractorName}
                clientLabelText={clientLabelText}
                quotationSubject={quotationSubject}
                quotationProject={quotationProject}
                quotationLocation={quotationLocation}
                quotationRevision={quotationRevision}
                quotationNotes={quotationNotes}
                quotationItems={quotationItems}
                paymentTerms={paymentTerms}
                attentionTo={attentionTo}
                subtotalAmount={subtotalAmount}
                discountPercent={discountPercent}
                vatPercent={vatPercent}
                incomeTaxPercent={incomeTaxPercent}
                totalAmount={totalAmount}
                publicUrl={publicUrl}
                headerLogo={headerLogo}
                isRTL={isRTL}
                t={t}
              />
            )
          )}
        </PdfPreviewModal>
      </div>
    </>
  );
};

// QuotationsPrintTemplate Component — single source of truth for single-record PDF
// Used both in the preview modal (no IDs) and as the hidden print template (page1Id/page2Id set).
const QuotationsPrintTemplate = ({
  quotationForPrintData,
  printQuotationDetails,
  quotationDate,
  contractorName,
  clientLabelText,
  quotationSubject,
  quotationProject,
  quotationLocation,
  quotationRevision,
  quotationNotes,
  quotationItems,
  paymentTerms,
  attentionTo,
  subtotalAmount,
  discountPercent,
  vatPercent,
  incomeTaxPercent,
  totalAmount,
  publicUrl,
  headerLogo,
  isRTL,
  t,
  page1Id,
  page2Id,
}) => {
  return (
    <>
      {/* First Page */}
      <div
        id={page1Id}
        style={{
          width: "794px",
          minHeight: "1123px",
          height: "auto",
          backgroundColor: "#ffffff",
          color: "#000000",
          fontFamily:
            '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: "bold",
          padding: "40px",
          boxSizing: "border-box",
          overflow: "visible",
        }}
      >
        {/* Outer thick border container */}
        <div
          style={{
            border: "5px solid #000",
            padding: "3px",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            minHeight: "1043px",
            height: "100%",
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Inner border container */}
          <div
            style={{
              border: "2px solid #000",
              minHeight: "1033px",
              height: "100%",
              padding: "18px 18px 10px",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              textRendering: "optimizeLegibility",
              direction: isRTL ? "rtl" : "ltr",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              overflow: "visible",
              flex: 1,
            }}
          >
            {/* Background Logo */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "100%",
                height: "600px",
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: "95%",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: "100%",
                flex: 1,
              }}
            >
              {/* Header */}
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                  marginBottom: "8px",
                  borderBottom: "4px double #000",
                  paddingBottom: "8px",
                  flexShrink: 0,
                }}
                crossOrigin="anonymous"
              />

              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "visible",
                  minHeight: 0,
                }}
              >
                {/* Info Table */}
                <div
                  style={{
                    marginTop: "12px",
                    border: "2px solid #000",
                    borderRadius: "4px",
                    overflow: "hidden",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                  }}
                >
                  {[
                    {
                      label: t("quotations.pdf.date", {
                        defaultValue: "التاريخ",
                      }),
                      value: quotationDate,
                      accent: true,
                    },
                    {
                      label: t("quotations.pdf.subject", {
                        defaultValue: "الموضوع",
                      }),
                      value: quotationSubject,
                    },
                    {
                      label: t("quotations.pdf.project", {
                        defaultValue: "المشروع",
                      }),
                      value: quotationProject,
                    },
                    ...(clientLabelText && contractorName
                      ? [{ label: clientLabelText, value: contractorName }]
                      : []),
                    {
                      label: t("quotations.pdf.location", {
                        defaultValue: "الموقع",
                      }),
                      value: quotationLocation,
                    },
                    {
                      label: t("quotations.pdf.revisionNumber", {
                        defaultValue: "رقم مراجعة",
                      }),
                      value: quotationRevision,
                      accent: true,
                    },
                  ]
                    .filter(row => row.value && row.value !== "—")
                    .map(row => (
                      <div
                        key={row.label}
                        style={{
                          display: "flex",
                          borderBottom: "1px solid #000",
                          minHeight: "38px",
                        }}
                      >
                        <div
                          style={{
                            width: "30%",
                            borderLeft: "1px solid #000",
                            backgroundColor: "#e3f2fd",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            textAlign: isRTL ? "right" : "left",
                            fontWeight: "700",
                            fontSize: "14px",
                            direction: isRTL ? "rtl" : "ltr",
                            color: "#1976d2",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            fontWeight: "bold",
                            padding: "0 10px",
                          }}
                        >
                          {row.label}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            textAlign: isRTL ? "right" : "left",
                            fontSize: "15px",
                            fontWeight: row.accent ? "700" : "500",
                            color: row.accent ? "#0057c2" : "#000",
                            direction: isRTL ? "rtl" : "ltr",
                            padding: "0 10px",
                            backgroundColor: "#ffffff",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            fontWeight: "bold",
                          }}
                        >
                          {row.value}
                        </div>
                      </div>
                    ))}
                </div>

                {/* Salutation */}
                {attentionTo && (
                  <div
                    style={{
                      marginTop: "18px",
                      direction: isRTL ? "rtl" : "ltr",
                      textAlign: isRTL ? "right" : "left",
                      fontSize: "13px",
                      lineHeight: 1.9,
                      fontFamily: '"Times New Roman", "Times", serif',
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontWeight: "700",
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                    >
                      {t("quotations.pdf.greeting", {
                        defaultValue: "السادة /",
                      })}{" "}
                      {attentionTo}{" "}
                      {t("quotations.pdf.respected", {
                        defaultValue: "المحترمين،",
                      })}
                    </p>
                  </div>
                )}

                {/* Introduction */}
                {printQuotationDetails?.introduction && (
                  <div
                    style={{
                      marginTop: "12px",
                      direction: isRTL ? "rtl" : "ltr",
                      textAlign: isRTL ? "right" : "left",
                      fontSize: "13px",
                      lineHeight: 1.9,
                      fontFamily: '"Times New Roman", "Times", serif',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: printQuotationDetails.introduction.replace(
                          /\n/g,
                          "<br />",
                        ),
                      }}
                    />
                  </div>
                )}

                {/* Notes */}
                {quotationNotes && quotationNotes.length > 0 && (
                  <div
                    style={{
                      marginTop: "18px",
                      direction: isRTL ? "rtl" : "ltr",
                      textAlign: isRTL ? "right" : "left",
                      fontSize: "13px",
                      lineHeight: 1.9,
                      fontFamily: '"Times New Roman", "Times", serif',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "700",
                        marginBottom: "8px",
                        fontSize: "14px",
                        color: "#1976d2",
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                    >
                      {t("quotations.pdf.notes", { defaultValue: "ملاحظات:" })}
                    </div>
                    <div
                      style={{
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                    >
                      {quotationNotes.map((note, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: "6px",
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                          {note}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Items Table */}
                {quotationItems && quotationItems.length > 0 && (
                  <div
                    style={{
                      marginTop: "18px",
                      direction: isRTL ? "rtl" : "ltr",
                      fontFamily: '"Times New Roman", "Times", serif',
                      pageBreakInside: "auto",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "12px",
                        border: "1px solid #000",
                        fontFamily: '"Times New Roman", "Times", serif',
                        pageBreakInside: "auto",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "#eef2f7",
                            pageBreakInside: "avoid",
                            pageBreakAfter: "auto",
                          }}
                        >
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            {t("quotations.pdf.serial", { defaultValue: "م" })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            {t("quotations.pdf.workItems", {
                              defaultValue: "بنود الاعمال",
                            })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            {t("quotations.pdf.category", {
                              defaultValue: "الفئة",
                            })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            {t("quotations.pdf.quantity", {
                              defaultValue: "الكمية",
                            })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            {t("quotations.pdf.unitPrice", {
                              defaultValue: "السعر جنيه",
                            })}
                          </th>
                          <th
                            style={{
                              border: "1px solid #000",
                              padding: "8px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            {t("quotations.pdf.totalPrice", {
                              defaultValue: "جنيه الإجمالي",
                            })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {quotationItems
                          .filter(item => item.item_type === "item")
                          .map((item, idx) => {
                            const total =
                              Number(item.qty || 0) *
                              Number(item.unit_price || 0);
                            let measurableDisplay = "—";
                            if (item.measurable) {
                              if (
                                typeof item.measurable === "object" &&
                                item.measurable !== null
                              ) {
                                measurableDisplay = item.measurable.symbol
                                  ? `${isRTL ? item.measurable.name_ar || item.measurable.name_en || "" : item.measurable.name_en || item.measurable.name_ar || ""} ${item.measurable.symbol}`
                                  : isRTL
                                    ? item.measurable.name_ar ||
                                    item.measurable.name_en ||
                                    "—"
                                    : item.measurable.name_en ||
                                    item.measurable.name_ar ||
                                    "—";
                              } else {
                                measurableDisplay = String(item.measurable);
                              }
                            } else if (item.unit) {
                              if (
                                typeof item.unit === "object" &&
                                item.unit !== null
                              ) {
                                measurableDisplay = item.unit.symbol
                                  ? `${isRTL ? item.unit.name_ar || item.unit.name_en || "" : item.unit.name_en || item.unit.name_ar || ""} ${item.unit.symbol}`
                                  : isRTL
                                    ? item.unit.name_ar ||
                                    item.unit.name_en ||
                                    item.unit.code ||
                                    "—"
                                    : item.unit.name_en ||
                                    item.unit.name_ar ||
                                    item.unit.code ||
                                    "—";
                              } else {
                                measurableDisplay = String(item.unit);
                              }
                            }
                            return (
                              <tr
                                key={idx}
                                style={{ pageBreakInside: "avoid" }}
                              >
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontFamily:
                                      '"Times New Roman", "Times", serif',
                                  }}
                                >
                                  {item.line_no || idx + 1}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    direction: isRTL ? "rtl" : "ltr",
                                    textAlign: isRTL ? "right" : "left",
                                    fontFamily:
                                      '"Times New Roman", "Times", serif',
                                  }}
                                >
                                  {isRTL
                                    ? item.description_ar ||
                                    item.description ||
                                    "—"
                                    : item.description_en ||
                                    item.description_ar ||
                                    item.description ||
                                    "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontFamily:
                                      '"Times New Roman", "Times", serif',
                                  }}
                                >
                                  {measurableDisplay}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontFamily:
                                      '"Times New Roman", "Times", serif',
                                  }}
                                >
                                  {item.qty || 0}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontFamily:
                                      '"Times New Roman", "Times", serif',
                                  }}
                                >
                                  {Number(item.unit_price || 0).toLocaleString(
                                    "en-US",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    },
                                  )}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "8px",
                                    textAlign: "center",
                                    fontFamily:
                                      '"Times New Roman", "Times", serif',
                                  }}
                                >
                                  {total.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Financial Summary */}
                <div
                  style={{
                    marginTop: "18px",
                    direction: isRTL ? "rtl" : "ltr",
                    fontFamily: '"Times New Roman", "Times", serif',
                    pageBreakInside: "avoid",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                      border: "1px solid #000",
                      fontFamily: '"Times New Roman", "Times", serif',
                      pageBreakInside: "avoid",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "10px",
                            textAlign: "center",
                            direction: isRTL ? "rtl" : "ltr",
                            fontWeight: "700",
                            width: "70%",
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                          {t("quotations.pdf.subtotal", {
                            defaultValue: "تكلفة إجمالي (بدون ضرائب)",
                          })}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "10px",
                            textAlign: "center",
                            fontWeight: "700",
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                          {subtotalAmount.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                      {discountPercent > 0 && (
                        <tr>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "10px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            {isRTL ? "قيمة الخصم" : "Discount"} (
                            {discountPercent}%)
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "10px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            —
                          </td>
                        </tr>
                      )}
                      {vatPercent > 0 && (
                        <tr>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "10px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            {isRTL ? "ضريبة القيمة المضافة" : "VAT"} (
                            {vatPercent}%)
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "10px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            —
                          </td>
                        </tr>
                      )}
                      {incomeTaxPercent > 0 && (
                        <tr>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "10px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            {isRTL ? "ضريبة الدخل" : "Income Tax"} (
                            {incomeTaxPercent}%)
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "10px",
                              textAlign: "center",
                              fontWeight: "700",
                              fontFamily: '"Times New Roman", "Times", serif',
                            }}
                          >
                            —
                          </td>
                        </tr>
                      )}
                      <tr style={{ backgroundColor: "#f6f7fb" }}>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "10px",
                            textAlign: "center",
                            direction: isRTL ? "rtl" : "ltr",
                            fontWeight: "700",
                            fontSize: "14px",
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                          {t("quotations.pdf.totalWithTax", {
                            defaultValue: "الإجمالي شاملة الضرائب",
                          })}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "10px",
                            textAlign: "center",
                            fontWeight: "700",
                            fontSize: "14px",
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                          {totalAmount.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer for first page */}
              <div
                style={{ marginTop: "auto", flexShrink: 0, paddingTop: "20px" }}
              >
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    borderTop: "4px double #000",
                    paddingTop: "8px",
                  }}
                  crossOrigin="anonymous"
                />
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "6px",
                    fontFamily: '"Times New Roman", "Times", serif',
                  }}
                >
                  <div
                    style={{
                      fontStyle: "italic",
                      marginTop: "2px",
                      display: "inline-flex",
                      alignItems: "baseline",
                      gap: "6px",
                      fontSize: "12px",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                    >
                      Page
                    </span>
                    <strong
                      style={{
                        fontSize: "13px",
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                    >
                      1 /{" "}
                      {paymentTerms ||
                        printQuotationDetails?.terms_and_conditions
                        ? "2"
                        : "1"}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Second Page - Payment Terms, Terms and Conditions, and Signatures */}
      {(paymentTerms || printQuotationDetails?.terms_and_conditions) && (
        <div
          id={page2Id}
          style={{
            width: "794px",
            minHeight: "1123px",
            height: "auto",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily:
              '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            padding: "40px",
            boxSizing: "border-box",
            overflow: "visible",
            marginTop: "20px",
          }}
        >
          {/* Outer thick border container */}
          <div
            style={{
              border: "5px solid #000",
              padding: "3px",
              backgroundColor: "#ffffff",
              boxSizing: "border-box",
              width: "100%",
              minHeight: "1043px",
              height: "auto",
              overflow: "visible",
            }}
          >
            {/* Inner border container */}
            <div
              style={{
                border: "2px solid #000",
                minHeight: "1033px",
                height: "100%",
                padding: "18px 18px 10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                textRendering: "optimizeLegibility",
                direction: isRTL ? "rtl" : "ltr",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                overflow: "visible",
              }}
            >
              {/* Background Logo */}
              <div
                style={{
                  position: "absolute",
                  top: "40%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "100%",
                  height: "600px",
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "95%",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "100%",
                  flex: 1,
                }}
              >
                {/* Header */}
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    marginBottom: "8px",
                    borderBottom: "4px double #000",
                    paddingBottom: "8px",
                    flexShrink: 0,
                  }}
                  crossOrigin="anonymous"
                />

                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    minHeight: 0,
                  }}
                >
                  {/* Payment Terms */}
                  {paymentTerms && (
                    <div
                      style={{
                        marginTop: "18px",
                        direction: isRTL ? "rtl" : "ltr",
                        fontSize: "13px",
                        lineHeight: 1.8,
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "700",
                          marginBottom: "8px",
                          fontSize: "14px",
                          color: "#1976d2",
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        {t("quotations.pdf.paymentTerms", {
                          defaultValue: "شروط الدفع:",
                        })}
                      </div>
                      <div
                        style={{
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: paymentTerms.replace(/\n/g, "<br />"),
                        }}
                      />
                    </div>
                  )}

                  {/* Terms and Conditions */}
                  {printQuotationDetails?.terms_and_conditions && (
                    <div
                      style={{
                        marginTop: "18px",
                        direction: isRTL ? "rtl" : "ltr",
                        fontSize: "13px",
                        lineHeight: 1.8,
                        fontFamily: '"Times New Roman", "Times", serif',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "700",
                          marginBottom: "8px",
                          fontSize: "14px",
                          color: "#1976d2",
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        {t("quotations.pdf.termsAndConditions", {
                          defaultValue: "الشروط والأحكام:",
                        })}
                      </div>
                      <div
                        style={{
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                        dangerouslySetInnerHTML={{
                          __html:
                            printQuotationDetails.terms_and_conditions.replace(
                              /\n/g,
                              "<br />",
                            ),
                        }}
                      />
                    </div>
                  )}

                  {/* Signatures Section */}
                  <div
                    style={{
                      marginTop: "18px",
                      marginBottom: "30px",
                      direction: isRTL ? "rtl" : "ltr",
                      fontSize: "13px",
                      lineHeight: 1.8,
                      fontFamily: '"Times New Roman", "Times", serif',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: "15px",
                        color: "#000",
                        fontWeight: "700",
                        fontSize: "13px",
                        fontFamily: '"Times New Roman", "Times", serif',
                        textAlign: "right",
                      }}
                    >
                      {t("quotations.pdf.inquiryNote", {
                        defaultValue:
                          "للمزيد من الاستفسارات يمكنكم الاتصال على المكتب للمزيد من الايضاح راجين المزيد من التعاون في المستقبل.",
                      })}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "40px",
                        flexWrap: "wrap",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: "0",
                          top: "-10px",
                          color: "#3333ff",
                          fontWeight: "700",
                          fontSize: "13px",
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        {t("quotations.pdf.respectNote", {
                          defaultValue: "وتفضلوا بقبول وافر الاحترام،،،،،،،،",
                        })}
                      </div>
                      <div
                        style={{
                          flex: "0 1 240px",
                          fontFamily: '"Times New Roman", "Times", serif',
                          color: "#3333ff",
                          fontWeight: "700",
                          marginTop: "20px",
                          textAlign: "center",
                        }}
                      >
                        <div style={{ fontSize: "13px", marginBottom: "6px" }}>
                          {t("quotations.pdf.financialManager", {
                            defaultValue: "المدير المالي",
                          })}
                        </div>
                        <div style={{ fontSize: "13px", marginBottom: "6px" }}>
                          أ/ معتز محمود
                        </div>
                        <div style={{ fontSize: "13px" }}>
                          {t("quotations.pdf.mobile", {
                            defaultValue: "موبايل",
                          })}
                          : 01005857456
                        </div>
                      </div>

                      <div
                        style={{
                          flex: "0 1 240px",
                          fontFamily: '"Times New Roman", "Times", serif',
                          color: "#3333ff",
                          fontWeight: "700",
                          marginTop: "20px",
                          textAlign: "center",
                        }}
                      >
                        <div style={{ fontSize: "13px", marginBottom: "6px" }}>
                          {t("quotations.pdf.executiveManager", {
                            defaultValue: "المدير التنفيذي",
                          })}
                        </div>
                        <div style={{ fontSize: "13px", marginBottom: "2px" }}>
                          أ/ فاطمة محمد حسن
                        </div>
                        <div style={{ fontSize: "13px" }}>
                          {t("quotations.pdf.mobile", {
                            defaultValue: "موبايل",
                          })}
                          : 01091938762
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: "28px",
                        textAlign: "center",
                        direction: isRTL ? "rtl" : "ltr",
                        fontFamily: '"Times New Roman", "Times", serif',
                        color: "#3333ff",
                        fontWeight: "700",
                      }}
                    >
                      <div style={{ fontSize: "15px", marginBottom: "12px" }}>
                        {t("quotations.pdf.generalManager", {
                          defaultValue: "مدير عام المكتب",
                        })}
                      </div>
                      <div
                        style={{
                          fontSize: "17px",
                          marginBottom: "8px",
                          fontWeight: "900",
                        }}
                      >
                        د. م. استشاري. محمد عبد اللطيف البرقاوي
                      </div>
                      <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                        مدرس ميكانيكا التربة والأساسات بجامعة السويس
                      </div>
                      <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                        دكتور الهندسة الجيوتقنية من كلية الهندسة المدنية - جامعة
                        ليدز - بريطانيا
                      </div>
                      <div style={{ fontSize: "12px", marginBottom: "8px" }}>
                        استشاري (الهندسة الجيوتقنية - الهندسة الجيولوجية وهندسة
                        الصخور - المساحة)
                      </div>
                      <div style={{ fontSize: "13px" }}>
                        {t("quotations.pdf.mobileWhatsApp", {
                          defaultValue: "موبايل / واتس اب",
                        })}
                        : 01000098958 -{" "}
                        {t("quotations.pdf.mobile", { defaultValue: "موبايل" })}
                        : 0155849077
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer for second page */}
                <div
                  style={{
                    marginTop: "auto",
                    flexShrink: 0,
                    paddingTop: "20px",
                  }}
                >
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      borderTop: "4px double #000",
                      paddingTop: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: "6px",
                      fontFamily: '"Times New Roman", "Times", serif',
                    }}
                  >
                    <div
                      style={{
                        fontStyle: "italic",
                        marginTop: "2px",
                        display: "inline-flex",
                        alignItems: "baseline",
                        gap: "6px",
                        fontSize: "12px",
                        fontFamily:
                          '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        fontWeight: "bold",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        Page
                      </span>
                      <strong
                        style={{
                          fontSize: "13px",
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        2 / 2
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ActionButton = ({ children, onClick, title, bg = "#2f3644" }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      backgroundColor: bg,
      color: "white",
      border: "none",
      borderRadius: "6px",
      padding: "8px 10px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "38px",
    }}
  >
    {children}
  </button>
);

// Print Template for All Quotations
// Dynamic Batch PDF page layout
const PdfPageInner = ({ chunk, startIdx, chunkIndex, totalChunks, showDate, isRTL, t, activeFiltersForPdf, headerLogo, publicUrl, dir, textAlign, formatCurrency, formatDate, renderStatusText }) => (
  <div style={{ border: '2px solid #000', height: '100%', padding: '18px', position: 'relative', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
    {/* Watermark */}
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${headerLogo})`, backgroundSize: '95%', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }} />
    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%', flex: 1 }}>
      {/* Header image */}
      <img src={`${publicUrl}/HeaderAce.png`} alt="Header"
        style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0 }}
        crossOrigin="anonymous"
      />
      {/* Content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, paddingBottom: '120px' }}>
        <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px', flexShrink: 0 }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
            {t('quotations.pdf.reportTitle', { defaultValue: isRTL ? 'تقرير عروض الأسعار' : 'Quotations Report' })}
          </h2>
          <p style={{ fontSize: '11px', color: '#555', margin: '4px 0 0' }}>
            {new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
            {totalChunks > 1 && ` — ${t('common.page', { defaultValue: 'Page' })} ${chunkIndex + 1} / ${totalChunks}`}
          </p>
          {activeFiltersForPdf && (
            <div style={{ fontSize: '11px', color: '#444', margin: '4px 0 0', padding: '4px 8px', backgroundColor: 'rgba(0,0,128,0.05)', borderRadius: '4px', display: 'inline-block' }}>
              {activeFiltersForPdf}
            </div>
          )}
        </div>
        <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', border: '1px solid #000', backgroundColor: 'rgba(255,255,255,0.7)', tableLayout: 'auto' }}>
            <thead>
              <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '36px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>#</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('quotations.tableHeaders.quotationNumber', { defaultValue: 'رقم العرض' })}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', width: '120px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('quotations.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: isRTL ? 'right' : 'left', fontWeight: '700', width: '120px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('quotations.tableHeaders.customer', { defaultValue: 'العميل' })}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('quotations.tableHeaders.itemsCount', { defaultValue: 'عدد البنود' })}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '90px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('quotations.tableHeaders.total', { defaultValue: 'الإجمالي' })}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '80px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('quotations.tableHeaders.status', { defaultValue: 'الحالة' })}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '90px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('quotations.tableHeaders.createdAt', { defaultValue: 'تاريخ الإنشاء' })}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '90px', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('quotations.tableHeaders.sentAt', { defaultValue: 'تاريخ الإرسال' })}</th>
              </tr>
            </thead>
            <tbody>
              {chunk.map((quotation, idx) => {
                const globalIndex = startIdx + idx + 1;
                const rowBg = idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)';
                const projectName = isRTL
                  ? quotation.project?.name_ar || quotation.project?.name_en || '—'
                  : quotation.project?.name_en || quotation.project?.name_ar || '—';
                const customerName =
                  quotation.customer_name ||
                  (quotation.client_label === "contractor"
                    ? isRTL
                      ? quotation.contractor?.name_ar || quotation.contractor?.name_en
                      : quotation.contractor?.name_en || quotation.contractor?.name_ar
                    : quotation.client_label === "customer"
                      ? isRTL
                        ? quotation.customer?.name_ar || quotation.customer?.name_en
                        : quotation.customer?.name_en || quotation.customer?.name_ar
                      : isRTL
                        ? quotation.contractor?.name_ar || quotation.contractor?.name_en || '—'
                        : quotation.contractor?.name_en || quotation.contractor?.name_ar || '—');
                return (
                  <tr key={quotation.id} style={{ backgroundColor: rowBg }}>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{globalIndex}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{quotation.quotation_no || quotation.quotation_number || quotation.id || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', backgroundColor: rowBg, whiteSpace: 'normal', maxWidth: '120px', wordBreak: 'break-word' }}>{projectName}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', backgroundColor: rowBg, whiteSpace: 'normal', maxWidth: '120px', wordBreak: 'break-word' }}>{customerName || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{quotation.items?.length || 0}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{formatCurrency(quotation.total_amount || quotation.subtotal_amount || quotation.subtotal)}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{renderStatusText(quotation.status)}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{formatDate(quotation.created_at)}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: rowBg }}>{formatDate(quotation.sent_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    {/* Footer — absolutely positioned, never overlaps table */}
    <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
      <img src={`${publicUrl}/FooterAce.png`} alt="Footer"
        style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px' }}
        crossOrigin="anonymous"
      />
    </div>
  </div>
);

export default Quotations;
